import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import PhoneInput from '../components/PhoneInput';
import ShippingMethodSelector from '../components/ShippingMethodSelector';
import PersonalizationEditor from '../components/PersonalizationEditor';

import { OrderData } from '@/models';
import { useAuth, useOrgaConfigurationContext } from '@/app/providers';
import { useOrder } from '@/app/providers';
import { getOrganizationLogoUrl } from '@/shared/utils/organization-logo';
import { useOrderJourneySecurity } from '@/shared/hooks/useOrderJourneySecurity';
import { useNavigation } from '@/shared/hooks/useNavigation';
import { useOrderPersonalization } from '@/shared/hooks/useOrderPersonalization';
import { usePermissionsHook } from '@/shared/hooks/usePermissionsHook';
import { useShippingMethods } from '../hooks/useShippingMethods';
import { PickupPoint } from '@/models';
import { PickupService } from '@/services/shipping/pickup-service';
import { FirebaseOrderService } from '@/services';
import { resolveShippingMethodConfigId } from '@/shared/utils/shippingMethodConfigId';
import { GOOGLE_MAPS_API_KEY } from '@/shared/config/environment';
import { Building2, Globe, Phone as PhoneIcon, MessageCircle, Instagram, Facebook, Plus, Trash2, MapPin, Linkedin, Twitter, Youtube, Mail, Palette, Info, Sparkles, Bell } from 'lucide-react';
import TikTokIcon from '@/components/icons/TikTokIcon';
import { googleMapsVerifier } from '@/services/google-maps/google-maps-verifier';
import SeoTags from '@/components/shared/SeoTags';
import { MarkdownText } from '@/components/ui/MarkdownText';

// Country labels are derived dynamically using Intl.DisplayNames

// Google Places Search Component
import { useLoadScript, Libraries } from "@react-google-maps/api";

const libraries: Libraries = ["places"];

// Helper pour extraire la première couleur d'un gradient ou retourner la couleur unie
const getPrimaryColor = (colorValue: string | undefined): string => {
  if (!colorValue) return '#9333EA';
  if (colorValue.includes('gradient') || colorValue.includes('linear-gradient')) {
    const colorsMatch = colorValue.match(/#[0-9A-Fa-f]{3,6}/g);
    if (colorsMatch && colorsMatch.length > 0) {
      return colorsMatch[0];
    }
  }
  return colorValue;
};

// Helper pour créer une couleur avec opacité (support gradient)
const getColorWithOpacity = (colorValue: string | undefined, opacity: number = 0.1): string => {
  const primaryColor = getPrimaryColor(colorValue);
  if (primaryColor.startsWith('#')) {
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return primaryColor;
};

const GooglePlacesSearch: React.FC<{
  onAddressSelect: (address: string, city: string, postalCode: string, country: string, houseNumber: string) => void;
  placeholder: string;
  autocompleteText: string;
  configuration?: any;
  availableShippingMethods?: any[];
  /** Pays déjà choisi dans le formulaire (ISO2) — prioritaire pour cadrer l'autocomplete */
  selectedFormCountry?: string | null;
  onGoogleMapsLoadSuccess?: () => void;
  onGoogleMapsLoadFailure?: () => void;
  isLoaded: boolean;
  loadError?: Error;
  initialValue?: string;
}> = ({ onAddressSelect, placeholder, autocompleteText, configuration, availableShippingMethods = [], selectedFormCountry, onGoogleMapsLoadSuccess, onGoogleMapsLoadFailure, isLoaded, loadError, initialValue }) => {
  const { t, i18n } = useTranslation();
  const personalization = useOrderPersonalization();
  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language || 'fr'], { type: 'region' });
    } catch {
      return new Intl.DisplayNames(['fr'], { type: 'region' });
    }
  }, [i18n.language]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(initialValue || "");
  // const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  // const placesService = useRef<google.maps.places.PlacesService | null>(null); // Deprecated
  const sessionToken = useRef<any>(null);
  // const placesServiceDiv = useRef<HTMLDivElement>(null); // Not needed for Place class

  const inputRef = useRef<HTMLInputElement>(null);
  const [authError, setAuthError] = useState(false);

  // Initialisation des services Google Maps
  useEffect(() => {
    if (isLoaded && !sessionToken.current && window.google && window.google.maps && window.google.maps.places) {
      // autocompleteService.current = new google.maps.places.AutocompleteService();
      // sessionToken.current = new google.maps.places.AutocompleteSessionToken();
      // PlacesService initialization removed
    }
  }, [isLoaded]);

  // Pays desservis par les méthodes actives (shippingConfig uniquement)
  const allowedCountries = useMemo(() => {
    const allAvailableCountries = new Set<string>();
    const methods = configuration?.shippingConfig?.methods;
    if (!Array.isArray(methods)) return [];
    for (const method of methods) {
      if (method?.isActive === false) continue;
      const list = method?.availableCountries;
      if (!Array.isArray(list) || list.length === 0) continue;
      for (const country of list) {
        if (country && String(country).trim()) {
          allAvailableCountries.add(String(country).trim().toUpperCase());
        }
      }
    }
    return Array.from(allAvailableCountries).map(c => c.toLowerCase());
  }, [configuration]);

  // Régions passées à Google Places : on garde le pays du formulaire en priorité,
  // mais on n'enferme pas l'autocomplete sur un seul pays (sinon impossible de chercher ES/BE après un premier choix FR).
  const includedRegionCodes = useMemo(() => {
    const fc = (selectedFormCountry || '').trim().toLowerCase();
    if (fc.length === 2 && /^[a-z]{2}$/.test(fc)) {
      if (allowedCountries.length === 0) return [fc];
      if (allowedCountries.includes(fc)) {
        return [fc, ...allowedCountries.filter(c => c !== fc)];
      }
    }
    if (allowedCountries.length > 0) {
      return allowedCountries;
    }
    return ['fr'];
  }, [allowedCountries, selectedFormCountry]);

  // Gestion des erreurs d'authentification
  useEffect(() => {
    const originalAuthFailure = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => {
      console.error("❌ Google Maps auth failure: invalid API key or restrictions");
      setAuthError(true);
      if (onGoogleMapsLoadFailure) {
        onGoogleMapsLoadFailure();
      }
      if (originalAuthFailure) originalAuthFailure();
    };

    return () => {
      (window as any).gm_authFailure = originalAuthFailure;
    };
  }, [onGoogleMapsLoadFailure]);

  // Vérification centralisée de la clé API
  useEffect(() => {
    const verifyApiKey = async () => {
      const isValid = await googleMapsVerifier.isApiKeyValid();
      if (!isValid) {
        setAuthError(true);
        if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
      } else {
        setAuthError(false);
      }
    };

    verifyApiKey();
  }, []);

  // Gestion des états de chargement
  useEffect(() => {
    if (loadError) {
      console.error("❌ Erreur de chargement Google Maps :", loadError.message);
      if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
    } else if (authError) {
      console.error("❌ Erreur d'authentification Google Maps");
      if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
    } else if (isLoaded) {
      console.log('[OrderForm][Autocomplete] Google chargé');
      setAuthError(false);
      if (onGoogleMapsLoadSuccess) onGoogleMapsLoadSuccess();
    }
  }, [isLoaded, loadError, authError, onGoogleMapsLoadSuccess, onGoogleMapsLoadFailure]);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (!value) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (isLoaded && window.google?.maps) {
      try {
        // @ts-ignore
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await google.maps.importLibrary("places");

        if (!sessionToken.current) {
          sessionToken.current = new AutocompleteSessionToken();
        }

        const request = {
          input: value,
          sessionToken: sessionToken.current,
          includedRegionCodes: includedRegionCodes,
          includedPrimaryTypes: ['street_address', 'route'],
        } as any;

        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        const filtered = (suggestions || []).filter((s: any) => {
          const types = s?.placePrediction?.types || [];
          return types.includes('street_address') || types.includes('route');
        });
        setSuggestions(filtered);
        console.log('[OrderForm][Autocomplete] suggestions reçues', { total: (suggestions || []).length, filtrées: filtered.length });
        setShowSuggestions(true);
      } catch (error) {
        console.error('Google Maps Autocomplete error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }
  };

  const handleSelectPrediction = async (placeId: string, description: string) => {
    setInputValue(description);
    setShowSuggestions(false);
    console.log('🖱️ Clique sur suggestion', { placeId, description });

    try {
      // Utilisation de la nouvelle classe Place (remplace PlacesService)
      // Note: sessionToken n'est pas supporté par fetchFields de la classe Place
      const place = new google.maps.places.Place({
        id: placeId,
        requestedLanguage: i18n.language || 'fr',
      });
      console.log('[OrderForm][Autocomplete] Place instanciée', { id: placeId, language: i18n.language || 'fr' });

      // Les nouveaux champs sont en camelCase : addressComponents, formattedAddress, location
      const result = await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location'],
      });
      console.log('[OrderForm][Autocomplete] fetchFields résultat', result);

      const placeData = result.place;
      console.log('[OrderForm][Autocomplete] formattedAddress', placeData?.formattedAddress || null);
      console.log('[OrderForm][Autocomplete] location', placeData?.location ? { lat: placeData.location.lat(), lng: placeData.location.lng() } : null);
      console.log('[OrderForm][Autocomplete] addressComponents (brut)', Array.isArray(placeData?.addressComponents) ? placeData.addressComponents.map((c: any) => ({ types: c.types, longText: c.longText, shortText: c.shortText })) : null);

      if (placeData && placeData.addressComponents) {
        let streetNumber = '';
        let route = '';
        let city = '';
        let postalCode = '';
        let country = '';

        // Parse address components (structure identique, mais noms de champs en camelCase ?)
        // Vérification : addressComponents est un tableau de AddressComponent
        // AddressComponent a : longText, shortText, types (nouveau API)
        // OU long_name, short_name, types (ancien API compatibilité ?)
        // La documentation dit : "Place data fields are formatted using camel case."
        // Mais AddressComponent structure : 
        // shortText (was short_name), longText (was long_name)

        for (const component of placeData.addressComponents) {
          const types = component.types;

          if (types.includes('street_number')) {
            streetNumber = component.longText || '';
          } else if (types.includes('route')) {
            route = component.longText || '';
          } else if (types.includes('locality')) {
            city = component.longText || '';
          } else if (types.includes('postal_code')) {
            postalCode = component.longText || '';
          } else if (types.includes('country')) {
            const countryCode = component.shortText || '';
            console.log('Pays détecté par Google:', component.longText, 'Code:', countryCode);
            country = countryCode || '';
          }
        }

        console.log('[OrderForm][Autocomplete] Composants extraits', { streetNumber, route, city, postalCode, country });

        // VALIDATION STRICTE : Rejeter les adresses sans route ou sans street_number
        if (!route) {
          console.warn('Sélection ignorée: pas de rue détectée (route manquante)');
          setInputValue('');
          onAddressSelect('', '', '', '', '');
          return;
        }

        if (!streetNumber || streetNumber.trim() === '') {
          console.warn('Sélection ignorée: pas de numéro de rue détecté (street_number manquant)');
          setInputValue('');
          onAddressSelect('', city || '', postalCode || '', country || '', '');
          return;
        }

        // Construct full address (on a maintenant route ET streetNumber garantis)
        const fullAddress = [streetNumber, route].filter(Boolean).join(' ');
        console.log('[OrderForm][Autocomplete] fullAddress construit', fullAddress);

        // Mettre à jour le champ de recherche avec l'adresse formatée complète de Google
        if (placeData.formattedAddress) {
          setInputValue(placeData.formattedAddress);
          console.log('[OrderForm][Autocomplete] inputValue mis à jour avec formattedAddress');
        }

        // Update form fields via callback (inclut houseNumber séparé)
        onAddressSelect(fullAddress, city, postalCode, country, streetNumber);

        // Refresh session token (toujours utile pour l'autocomplete suivant)
        // @ts-ignore
        const { AutocompleteSessionToken } = await google.maps.importLibrary("places");
        sessionToken.current = new AutocompleteSessionToken();
      } else {
        console.error('Google Maps Place details fetch failed or incomplete');
      }
    } catch (error) {
      console.error('Google Maps Place details error:', error);
    }
  };

  if (loadError) {
    return null; // Le parent gérera l'affichage manuel via le callback onGoogleMapsLoadFailure
  }

  if (!isLoaded) {
    return (
      <div
        className="w-full px-6 py-4 border rounded-2xl animate-pulse"
        style={{
          borderColor: personalization.input.border,
          color: personalization.text.secondary,
        }}
      >
        {t('orderForm.google.loading')}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Fake input to trick Chrome autofill */}
      <input type="text" style={{ display: 'none' }} autoComplete="address-line1" />

      <input
        ref={inputRef}
        type="text"
        name="input_1"
        id="address-input-google"
        autoComplete="new-password"
        data-lpignore="true"
        data-1p-ignore="true"
        className={`w-full px-6 py-4 border rounded-2xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${personalization.input.borderRadius || 'rounded-2xl'}`}
        style={{
          backgroundColor: personalization.input.background,
          borderColor: personalization.input.border,
          color: personalization.input.text,
          '--placeholder-color': personalization.input.placeholder,
        } as any}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={(e) => {
          if (inputValue && suggestions.length > 0) setShowSuggestions(true);
          if (personalization.input.focusBorder) {
            e.currentTarget.style.borderColor = personalization.input.focusBorder;
          }
          if (personalization.input.focusRing) {
            (e.currentTarget.style as any)['--tw-ring-color'] = personalization.input.focusRing;
          }
        }}
        onBlur={(e) => {
          setTimeout(() => setShowSuggestions(false), 200);
          e.currentTarget.style.borderColor = personalization.input.border;
        }}
      />

      {showSuggestions && suggestions.length > 0 && (
        <ul
          className="absolute z-50 w-full border rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto"
          style={{
            backgroundColor: personalization.card.background || '#FFFFFF',
            borderColor: personalization.card.border || '#E5E7EB',
          }}
        >
          {suggestions.map((suggestion: any) => (
            <li
              key={suggestion.placePrediction.placeId}
              className="px-4 py-2 cursor-pointer text-sm transition-colors"
              style={{
                color: personalization.text.primary,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = getColorWithOpacity(personalization.button.primary, 0.1);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={() => handleSelectPrediction(suggestion.placePrediction.placeId, suggestion.placePrediction.text.text)}
            >
              {suggestion.placePrediction.text.text}
            </li>
          ))}
        </ul>
      )}

      <p
        className="mt-1 text-xs"
        style={{ color: personalization.text.secondary }}
      >
        {autocompleteText}
      </p>
    </div>
  );
};

const MemoizedGooglePlacesSearch = React.memo(GooglePlacesSearch);

// Define types for the unified form
type UnifiedFormData = {
  // Order details
  tiktokUsername: string;
  amount: string;
  productReference: string;
  productItems: Array<{
    modelName?: string;
    size?: string;
    customFields?: Record<string, string>;
  }>;
  email: string;
  // Personal information
  lastName: string;
  firstName: string;
  phone: string;
  address: string;
  fullAddress?: string;
  houseNumber: string; // Numéro de rue extrait de Google Maps (OBLIGATOIRE)
  city: string;
  postalCode: string;
  country: string | null;
  // Delivery method
  deliveryMethod: string | null;
  // Pickup point
  pickupPoint?: PickupPoint;
};

const OrderForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const { orderData, setOrderData, saveOrder, isLoading, currentOrderId, isMergedOrder, setIsMergedOrder, selectedPickupPoint: contextSelectedPickupPoint, setSelectedPickupPoint: setContextSelectedPickupPoint, clearOrder, isOrderComplete, currentStep } = useOrder();
  const { organizationId, navigateToPayment } = useNavigation();
  const {
    configuration,
    loading
  } = useOrgaConfigurationContext();
  const personalization = useOrderPersonalization();
  const { canManageConfiguration } = usePermissionsHook();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPersonalizationEditMode = searchParams.get('edit') === 'personalization';

  // Vérifier si l'utilisateur a les droits pour gérer la personnalisation
  const canManagePersonalization = useMemo(() => {
    if (!currentUser || !organizationId) return false;
    return canManageConfiguration(organizationId);
  }, [currentUser, organizationId, canManageConfiguration]);

  // Empêcher l'accès au mode édition si l'utilisateur n'a pas les droits
  useEffect(() => {
    if (isPersonalizationEditMode && !canManagePersonalization) {
      // Retirer le paramètre edit de l'URL si l'utilisateur n'a pas les droits
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
      toast.error(t('admin.configuration.personalization.noPermission') || 'Vous n\'avez pas les droits pour accéder à la personnalisation');
    }
  }, [isPersonalizationEditMode, canManagePersonalization, searchParams, setSearchParams, t]);

  // Réinitialiser le contexte commande quand on revient sur le formulaire (step 1) après une commande terminée,
  // pour éviter d'afficher les données de l'ancienne commande (cache) au lieu d'un formulaire vide.
  useEffect(() => {
    if (isOrderComplete || currentStep === 'complete') {
      clearOrder();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount when landing on step 1

  // Helper function pour générer les styles des cartes
  const getCardStyles = () => {
    const style: React.CSSProperties = {};
    const classes: string[] = [];

    if (personalization.card.background) {
      style.backgroundColor = personalization.card.background;
    }
    if (personalization.card.border) {
      style.borderColor = personalization.card.border;
      style.borderWidth = '1px';
      style.borderStyle = 'solid';
    }
    if (personalization.card.borderRadius && !personalization.card.borderRadius.includes('#')) {
      classes.push(personalization.card.borderRadius);
    } else if (!personalization.card.borderRadius) {
      classes.push('rounded-3xl');
    }
    if (personalization.card.shadow && !personalization.card.shadow.includes('#')) {
      classes.push(personalization.card.shadow);
    } else if (!personalization.card.shadow) {
      classes.push('shadow-lg');
    }

    return { style, className: classes.join(' ') };
  };

  // Helper function pour générer les styles des inputs
  const getInputStyles = () => {
    const style: React.CSSProperties = {};
    const classes: string[] = ['border', 'focus:outline-none', 'transition-all'];

    if (personalization.input.background) {
      style.backgroundColor = personalization.input.background;
    }
    if (personalization.input.border) {
      style.borderColor = personalization.input.border;
    }
    if (personalization.input.text) {
      style.color = personalization.input.text;
    }
    if (personalization.input.placeholder) {
      // Utiliser une variable CSS via l'objet style React (syntaxe pour les variables CSS)
      (style as any)['--placeholder-color'] = personalization.input.placeholder;
    }
    // Pour le focus, on utilise les classes Tailwind standard mais avec les couleurs personnalisées
    if (personalization.input.focusBorder || personalization.input.focusRing) {
      classes.push('focus:ring-2');
    }
    if (personalization.input.borderRadius && !personalization.input.borderRadius.includes('#')) {
      classes.push(personalization.input.borderRadius);
    } else if (!personalization.input.borderRadius) {
      classes.push('rounded-2xl');
    }

    return { style, className: classes.join(' ') };
  };

  // Helper pour obtenir le style inline pour les inputs avec focus
  const getInputFocusStyles = (): React.CSSProperties => {
    const style: React.CSSProperties = {};
    if (personalization.input.focusBorder) {
      style.borderColor = personalization.input.focusBorder;
    }
    if (personalization.input.focusRing) {
      // Utiliser une variable CSS via l'objet style React
      (style as any)['--tw-ring-color'] = personalization.input.focusRing;
      // Ajouter aussi la couleur de ring via une classe CSS custom si nécessaire
      (style as any)['--tw-ring-opacity'] = '1';
    }
    return style;
  };

  // Helper pour appliquer une couleur ou un gradient
  const applyBackgroundStyle = (element: HTMLElement, value: string) => {
    if (value && (value.includes('gradient') || value.includes('linear-gradient'))) {
      element.style.setProperty('background', value, 'important');
      element.style.removeProperty('background-color');
    } else if (value) {
      element.style.setProperty('background-color', value, 'important');
      element.style.removeProperty('background');
      element.style.removeProperty('background-image');
    }
    // TOUJOURS forcer la couleur du texte avec !important pour éviter qu'elle soit écrasée
    element.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
  };

  // Helper function pour générer les styles des boutons
  const getButtonStyles = (variant: 'primary' | 'ghost' = 'primary') => {
    if (variant === 'ghost') return { style: {}, className: '' };

    const style: React.CSSProperties = {};
    const classes: string[] = ['font-semibold', 'transition-all', 'shadow-lg'];

    if (personalization.button.primary) {
      // Support des gradients : utiliser background au lieu de backgroundColor
      if (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient')) {
        style.background = personalization.button.primary;
      } else {
        style.backgroundColor = personalization.button.primary;
      }
    }
    if (personalization.button.primaryText) {
      style.color = personalization.button.primaryText;
    }
    if (personalization.button.primaryHover) {
      // Support des gradients pour le hover
      if (personalization.button.primaryHover.includes('gradient') || personalization.button.primaryHover.includes('linear-gradient')) {
        (style as any)['--hover-gradient'] = personalization.button.primaryHover;
      } else {
        (style as any)['--hover-color'] = personalization.button.primaryHover;
      }
    }
    if (personalization.button.borderRadius && !personalization.button.borderRadius.includes('#')) {
      classes.push(personalization.button.borderRadius);
    } else if (!personalization.button.borderRadius) {
      classes.push('rounded-2xl');
    }

    return { style, className: classes.join(' ') };
  };

  // Helper function pour générer les styles des badges
  const getBadgeStyles = () => {
    const style: React.CSSProperties = {};
    const classes: string[] = ['inline-flex', 'items-center', 'px-3', 'sm:px-4', 'py-2', 'text-xs', 'sm:text-sm', 'font-medium'];

    if (personalization.badge.background) {
      style.backgroundColor = personalization.badge.background;
    }
    if (personalization.badge.text) {
      style.color = personalization.badge.text;
    }
    if (personalization.badge.borderRadius && !personalization.badge.borderRadius.includes('#')) {
      classes.push(personalization.badge.borderRadius);
    } else if (!personalization.badge.borderRadius) {
      classes.push('rounded-full');
    }

    return { style, className: classes.join(' ') };
  };

  // Security check for organization consistency
  const {
    hasOrganizationChanged
  } = useOrderJourneySecurity();

  // Shipping methods management
  const {
    availableShippingMethods,
    getShippingMethodsForCountry,
    getShippingMethodById
  } = useShippingMethods();

  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language || 'fr'], { type: 'region' });
    } catch {
      return new Intl.DisplayNames(['fr'], { type: 'region' });
    }
  }, [i18n.language]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    for (const method of availableShippingMethods || []) {
      for (const c of method.availableCountries || []) {
        set.add(String(c).toUpperCase());
      }
    }
    return Array.from(set);
  }, [availableShippingMethods]);

  // Determine if it's GlowLive based on configuration
  const isGlowLive = configuration?.organizationId === 'glowlive';

  const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
  const [selectedPickupPoint, setSelectedPickupPoint] = useState<PickupPoint | null>(null);
  const [loadingPickupPoints, setLoadingPickupPoints] = useState(false);
  const [pickupSearchPostalCode, setPickupSearchPostalCode] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [hasSearchedPickupPoints, setHasSearchedPickupPoints] = useState(false);
  const postalCodeInputRef = useRef<HTMLInputElement>(null);
  const [lastValidEmail, setLastValidEmail] = useState<string>('');
  const [countryDisplayName, setCountryDisplayName] = useState<string>('');
  const [autoSelected, setAutoSelected] = useState(false);
  const [isGoogleAvailable, setIsGoogleAvailable] = useState(true);

  const [rememberMyInfo, setRememberMyInfo] = useState(false);
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [showManualAddress, setShowManualAddress] = useState(false);
  const hasLoadedCustomerInfo = useRef(false);

  // Helper to get contact link
  const getContactLink = (type: string, value: string) => {
    switch (type) {
      case 'email':
        return `mailto:${value}`;
      case 'phone':
        return `tel:${value}`;
      case 'whatsapp':
        return `https://wa.me/${value.replace(/\+/g, '').replace(/\s/g, '')}`;
      default:
        return '#';
    }
  };

  // Helper to get contact icon
  const getContactIcon = (type: string) => {
    switch (type) {
      case 'email': return Mail;
      case 'phone': return PhoneIcon;
      case 'whatsapp': return MessageCircle;
      default: return Globe;
    }
  };

  const getSocialIcon = (key: string) => {
    switch (key.toLowerCase()) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'tiktok': return TikTokIcon;
      case 'linkedin': return Linkedin;
      case 'twitter': return Twitter;
      case 'youtube': return Youtube;
      case 'website': return Globe;
      case 'pinterest': return Globe;
      case 'snapchat': return MessageCircle;
      default: return Globe;
    }
  };

  // Combine SAV contacts (new dynamic list) with legacy fallbacks if list is empty
  const savContacts = useMemo(() => {
    const contacts = [...(configuration?.afterSalesService?.contacts || [])];

    // If no dynamic contacts, try legacy fields
    if (contacts.length === 0 && configuration) {
      // Use any cast to access potentially existing legacy fields during migration
      const legacySav = configuration.afterSalesService as any;

      if (legacySav?.email || configuration.email) {
        contacts.push({ type: 'email' as const, value: legacySav?.email || configuration.email || '', label: 'Email' });
      }
      if (legacySav?.phone || configuration.phone) {
        contacts.push({ type: 'phone' as const, value: legacySav?.phone || configuration.phone || '', label: 'Téléphone' });
      }
      if (legacySav?.whatsapp || configuration.whatsappNumber) {
        contacts.push({ type: 'whatsapp' as const, value: legacySav?.whatsapp || configuration.whatsappNumber || '', label: 'WhatsApp' });
      }
    }

    return contacts;
  }, [configuration]);

  // Génère le texte explicatif de la configuration de fusion des commandes
  const getMergeConfigInfo = useMemo(() => {
    const mergeConfig = configuration?.shippingConfig?.orderMergeConfig;
    const ranges = mergeConfig?.mergeOpenDaysRanges || [];
    
    if (ranges.length === 0) {
      return { hasConfig: true, text: t('orderForm.mergeInfo.sameDayOnly') };
    }
    
    const weekdays = [
      t('admin.configuration.shipping.orderMergeConfig.weekday0'),
      t('admin.configuration.shipping.orderMergeConfig.weekday1'),
      t('admin.configuration.shipping.orderMergeConfig.weekday2'),
      t('admin.configuration.shipping.orderMergeConfig.weekday3'),
      t('admin.configuration.shipping.orderMergeConfig.weekday4'),
      t('admin.configuration.shipping.orderMergeConfig.weekday5'),
      t('admin.configuration.shipping.orderMergeConfig.weekday6'),
    ];
    
    const rangeTexts = ranges
      .filter((r: { start?: number | null; end?: number | null }) => r.start != null && r.end != null)
      .map((r: { start: number; end: number }) => {
        if (r.start === r.end) {
          return weekdays[r.start];
        }
        return t('orderForm.mergeInfo.rangeFromTo', { start: weekdays[r.start], end: weekdays[r.end] });
      });
    
    if (rangeTexts.length === 0) {
      return { hasConfig: true, text: t('orderForm.mergeInfo.sameDayOnly') };
    }
    
    const rangeStr = rangeTexts.length === 1 
      ? `du ${rangeTexts[0]}`
      : rangeTexts.map((r: string, i: number) => i === rangeTexts.length - 1 ? `et du ${r}` : `du ${r}`).join(', ');
    
    return { hasConfig: true, text: t('orderForm.mergeInfo.withOpenDays', { range: rangeStr }) };
  }, [configuration, t]);

  const parseProductModelToItems = (model: string | undefined) => {
    if (!model || !model.trim()) return [{ modelName: '', size: '' }];
    return model
      .split('+')
      .map(part => part.trim())
      .filter(Boolean)
      .map(part => {
        if (part.includes('-')) {
          const [m, ...rest] = part.split('-');
          return { modelName: m.trim(), size: rest.join('-').trim() };
        }
        const tokens = part.split(/\s+/).filter(Boolean);
        if (tokens.length > 1) {
          const s = tokens.pop() as string;
          return { modelName: tokens.join(' ').trim(), size: s.trim() };
        }
        return { modelName: tokens[0] || '', size: '' };
      });
  };

  const deriveStreetNameFromAddress = (addr: string, hn: string) => {
    const a = String(addr || '').trim();
    const h = String(hn || '').trim();
    if (!a) return '';
    if (!h) return a;
    const escaped = h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^\n?\r?\s*${escaped}\s*[,\-]*\s*`, 'i');
    const s = a.replace(re, '').trim();
    if (s) return s;
    return a.replace(/^\s*\d+\s*[,\-]*\s*/, '').trim();
  };

  const libraries = useMemo<("places")[]>(() => ["places"], []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  useEffect(() => {
    console.log('[OrderForm] Présence clé Google (non affichée) =', Boolean(GOOGLE_MAPS_API_KEY));
  }, []);

  // Unified form for all data
  const form = useForm<UnifiedFormData>({
    mode: 'onChange',
    defaultValues: {
      tiktokUsername: orderData.tiktokUsername || '',
      amount: orderData.amount || '',
      productReference: orderData.productReference || '',
      productItems: orderData.productItems && orderData.productItems.length > 0
        ? orderData.productItems
        : parseProductModelToItems(orderData.productModel),
      email: currentUser?.email || orderData.email || '',
      lastName: orderData.lastName || '',
      firstName: orderData.firstName || '',
      phone: orderData.phone || '',
      address: orderData.address || '',
      fullAddress: (orderData as any).fullAddress || '',
      houseNumber: orderData.houseNumber || '',
      city: orderData.city || '',
      postalCode: orderData.postalCode || '',
      country: orderData.country || null,
      deliveryMethod: orderData.deliveryMethod || null,
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "productItems"
  });

  // Performance: Watch specific fields instead of everything
  // const watchedValues = form.watch(); // REMOVED to prevent global re-renders
  const watchedDeliveryMethod = form.watch('deliveryMethod');
  const watchedCountry = form.watch('country');
  const watchedEmail = form.watch('email');
  const watchedAmount = form.watch('amount');
  const watchedProductReference = form.watch('productReference');
  const watchedTiktokUsername = form.watch('tiktokUsername');

  useEffect(() => {
    // Log uniquement si le point relais change pour éviter le spam
    if (selectedPickupPoint) {
      // console.log('📍 Selected pickup point:', selectedPickupPoint);
    }
  }, [selectedPickupPoint]);

  // Callbacks pour GooglePlacesSearch stabilisés
  const handleGoogleMapsLoadSuccess = React.useCallback(() => {
    // console.log('✅ Google Maps chargé - mode autocomplete activé');
    setIsGoogleAvailable(true);
  }, []);

  const handleGoogleMapsLoadFailure = React.useCallback(() => {
    console.log('❌ Google Maps non disponible');
    setAutoSelected(false);
    setIsGoogleAvailable(false);
  }, []);

  const handleAddressSelect = React.useCallback((address: string, city: string, postalCode: string, country: string, houseNumber: string) => {
    console.log('Adresse sélectionnée:', { address, city, postalCode, country, houseNumber });

    if (!houseNumber || houseNumber.trim() === '') {
      form.setError("houseNumber", {
        type: "manual",
        message: t('orderForm.steps.shippingInfo.address.missingHouseNumber')
      });
      return;
    }

    const streetOnly = deriveStreetNameFromAddress(address, houseNumber);

    // Capture previous country logic for reset
    const previousCountry = form.getValues("country");
    const newCountryNormalized = (country || '').toUpperCase();

    if (previousCountry && newCountryNormalized && previousCountry !== newCountryNormalized) {
      console.log('🌍 Changement de pays détecté:', previousCountry, '->', newCountryNormalized);
      setSelectedPickupPoint(null);
      setContextSelectedPickupPoint(null);
      setPickupPoints([]);
      setHasSearchedPickupPoints(false);
      setPickupSearchPostalCode(postalCode || '');
      // FORCE reset of delivery method
      form.setValue("deliveryMethod", null);
    }

    form.setValue("address", streetOnly);
    form.setValue("fullAddress", address);
    form.setValue("city", city);
    form.setValue("postalCode", postalCode);
    form.setValue("houseNumber", houseNumber);
    setAutoSelected(true);

    form.clearErrors("houseNumber");

    if (postalCode) {
      setPickupSearchPostalCode(postalCode);
      if (postalCodeInputRef.current) {
        postalCodeInputRef.current.value = postalCode;
      }
    }

    const normalizedCountry = (country || '').toUpperCase();
    const countryName = regionNames.of(normalizedCountry) || normalizedCountry;
    setCountryDisplayName(countryName as string);

    const availableMethodsForCountry = getShippingMethodsForCountry(normalizedCountry);
    const isCountryAvailable = availableMethodsForCountry.length > 0;

    if (isCountryAvailable) {
      console.log(`✅ Pays ${normalizedCountry} disponible`);
      const previousCountry = form.getValues("country");

      form.setValue("country", normalizedCountry || null);

      if (previousCountry !== normalizedCountry) {
        // Reset du point relais
        setSelectedPickupPoint(null);
        setContextSelectedPickupPoint(null);
        form.setValue('pickupPoint', undefined, { shouldValidate: true });
        setPickupPoints([]);
        setHasSearchedPickupPoints(false);
        setPickupSearchPostalCode(postalCode || '');

        // Reset de la méthode de livraison (forcer l'utilisateur à rechoisir ou laisser vide)
        form.setValue("deliveryMethod", null);
      } else {
        // Si le pays est le même mais qu'on n'a pas de méthode (cas rare), on peut forcer la première
        // ou laisser tel quel. Dans le doute, on ne touche pas si le pays n'a pas changé.
      }

      // Auto-sélection de la première méthode si aucune n'est sélectionnée ? 
      // L'utilisateur a demandé un RESET, donc on laisse à null si ça a changé.
      // Si on veut être gentil :
      // if (!form.getValues("deliveryMethod") && availableMethodsForCountry.length > 0) {
      //   form.setValue("deliveryMethod", availableMethodsForCountry[0].id as any);
      // }
      // Mais respectons la demande "reset".
    } else {
      console.log(`⚠️ Pays ${normalizedCountry} non disponible`);
      form.setValue("country", normalizedCountry || null);
    }

    setTimeout(() => {
      form.trigger(['address', 'city', 'postalCode', 'country', 'deliveryMethod', 'houseNumber']);
    }, 100);

    setIsEditingAddress(false);
  }, [form, t, regionNames, getShippingMethodsForCountry]);

  const handleMethodChange = React.useCallback((methodId: string) => {
    form.setValue("deliveryMethod", methodId as any, { shouldValidate: true });

    // Vérifier si c'est une livraison à domicile depuis la configuration
    const method = getShippingMethodById(methodId);
    const isHomeDelivery = method?.isHomeDelivery === true;

    // Si on passe à une livraison à domicile, réinitialiser le point relais
    if (isHomeDelivery) {
      setSelectedPickupPoint(null);
      setContextSelectedPickupPoint(null);
    }
  }, [form, getShippingMethodById, setSelectedPickupPoint, setContextSelectedPickupPoint]);



  // Calculer la validation du bouton avec useMemo pour qu'elle se mette à jour quand les dépendances changent
  const buttonValidation = useMemo(() => {
    // Pour les commandes fusionnées, validation simple
    if (isMergedOrder) {
      const basicFieldsValid = watchedAmount &&
        watchedProductReference &&
        watchedEmail &&
        watchedTiktokUsername;
      return {
        canSubmit: basicFieldsValid && !isLoading,
        isPickupRequired: false,
        deliveryMethodRequired: false,
        hasDeliveryMethod: true // Pour les commandes fusionnées, on considère qu'il y a un mode de livraison (déjà configuré)
      };
    }

    // Pour les nouvelles commandes
    const selectedMethod = getShippingMethodById(watchedDeliveryMethod || '');
    const hasDeliveryMethod = !!watchedDeliveryMethod;
    const methodsForCountry = watchedCountry ? getShippingMethodsForCountry(watchedCountry) : [];
    const isHomeDelivery = selectedMethod?.isHomeDelivery === true;
    const isPickupMethod = !isHomeDelivery && selectedMethod && !!selectedMethod;
    const deliveryMethodRequired = watchedCountry && methodsForCountry.length > 0 && !hasDeliveryMethod;
    const isFormValid = form.formState.isValid && !isLoading;
    const isPickupRequired = isPickupMethod && (selectedPickupPoint === null || selectedPickupPoint === undefined);
    const canSubmit = isFormValid &&
      !isLoading &&
      !deliveryMethodRequired &&
      !isPickupRequired &&
      hasDeliveryMethod;

    return { canSubmit, isPickupRequired, deliveryMethodRequired, hasDeliveryMethod };
  }, [watchedDeliveryMethod, watchedCountry, isMergedOrder, selectedPickupPoint, form.formState.isValid, isLoading, watchedAmount, watchedProductReference, watchedEmail, watchedTiktokUsername]);

  // Auto/Manual address effects (must run AFTER form is initialized)
  // Removed isManualAddress effect

  useEffect(() => {
    const values = form.getValues();
    const hasAuto = Boolean(values.address && values.houseNumber && values.city && values.postalCode && values.country);
    setAutoSelected(hasAuto);
  }, [form]);

  useEffect(() => {
    const subscription = form.watch((value) => {
      const address = (value.address || '') as string;
      const houseNumber = (value.houseNumber || '') as string;
      const city = (value.city || '') as string;
      const postalCode = (value.postalCode || '') as string;
      const country = value.country as any;
      const hasAuto = Boolean(address.trim() !== '' && houseNumber.trim() !== '' && city.trim() !== '' && postalCode.trim() !== '' && country && String(country).trim() !== '');
      setAutoSelected(hasAuto);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Removed auto selection switching effect

  // Sync selectedPickupPoint with form and context
  useEffect(() => {
    form.setValue('pickupPoint', selectedPickupPoint || undefined, { shouldValidate: true });
    // Synchroniser aussi avec le contexte pour qu'il soit disponible dans PaymentSelection
    if (selectedPickupPoint !== contextSelectedPickupPoint) {
      setContextSelectedPickupPoint(selectedPickupPoint);
    }
  }, [selectedPickupPoint, form, contextSelectedPickupPoint, setContextSelectedPickupPoint]);

  // Restaurer le point relais depuis orderData ou le contexte quand on revient en arrière depuis la page de paiement
  useEffect(() => {
    // Priorité 1: Si le contexte a un point relais, l'utiliser
    if (contextSelectedPickupPoint && !selectedPickupPoint) {
      console.log('📍 Restauration du point relais depuis le contexte:', contextSelectedPickupPoint);
      setSelectedPickupPoint(contextSelectedPickupPoint);

      // Ajouter le point sélectionné à la liste des points relais s'il n'y est pas déjà
      setPickupPoints(prevPoints => {
        const exists = prevPoints.some(p => p.id === contextSelectedPickupPoint.id);
        if (!exists) {
          return [contextSelectedPickupPoint];
        }
        return prevPoints;
      });

      // Restaurer aussi le code postal de recherche si disponible
      if (contextSelectedPickupPoint.postalCode) {
        setPickupSearchPostalCode(contextSelectedPickupPoint.postalCode);
        if (postalCodeInputRef.current) {
          postalCodeInputRef.current.value = contextSelectedPickupPoint.postalCode;
        }
      }

      // Marquer qu'une recherche a été effectuée pour afficher les résultats
      setHasSearchedPickupPoints(true);
    }
    // Priorité 2: Si orderData contient un pickupPoint et qu'aucun n'est actuellement sélectionné
    else if (orderData.pickupPoint && !selectedPickupPoint) {
      console.log('📍 Restauration du point relais depuis orderData:', orderData.pickupPoint);
      setSelectedPickupPoint(orderData.pickupPoint);

      // Synchroniser aussi avec le contexte
      setContextSelectedPickupPoint(orderData.pickupPoint);

      // Ajouter le point sélectionné à la liste des points relais s'il n'y est pas déjà
      setPickupPoints(prevPoints => {
        const exists = prevPoints.some(p => p.id === orderData.pickupPoint?.id);
        if (!exists && orderData.pickupPoint) {
          return [orderData.pickupPoint];
        }
        return prevPoints;
      });

      // Restaurer aussi le code postal de recherche si disponible
      if (orderData.pickupPoint.postalCode) {
        setPickupSearchPostalCode(orderData.pickupPoint.postalCode);
        if (postalCodeInputRef.current) {
          postalCodeInputRef.current.value = orderData.pickupPoint.postalCode;
        }
      }

      // Marquer qu'une recherche a été effectuée pour afficher les résultats
      setHasSearchedPickupPoints(true);
    }
  }, [orderData.pickupPoint, selectedPickupPoint, contextSelectedPickupPoint, setContextSelectedPickupPoint]);

  // Réinitialiser le point relais quand on change de méthode de livraison
  useEffect(() => {
    if (!isMergedOrder) {
      const currentMethod = getShippingMethodById(watchedDeliveryMethod || '');
      // Si la méthode est à domicile ET qu'un point relais est sélectionné, on le vire
      if (currentMethod?.isHomeDelivery && selectedPickupPoint) {
        console.log('🚚 Changement vers livraison à domicile : suppression du point relais');
        setSelectedPickupPoint(null);
        setContextSelectedPickupPoint(null);
      }
    }
  }, [isMergedOrder, watchedDeliveryMethod, selectedPickupPoint, setContextSelectedPickupPoint]);

  // Update form when orderData changes (for pre-filling)
  useEffect(() => {
    // Si c'est une commande fusionnée, NE PAS faire de reset du formulaire
    // Les champs sont déjà mis à jour individuellement dans la logique de fusion
    if (isMergedOrder) {
      console.log('🔄 Commande fusionnée détectée, PAS de reset du formulaire');
      console.log('  - Les champs sont déjà mis à jour individuellement');
      return; // Sortir sans faire de reset
    }

    // Vérifier si l'utilisateur a déjà saisi des données dans le formulaire
    const currentFormValues = form.getValues();
    const modelFormat = configuration?.personalization?.productModelConfig?.format || configuration?.productModelConfig?.format || 'structured';
    const isCustomFormat = modelFormat === 'custom';
    const customFields = configuration?.personalization?.productModelConfig?.customFields || configuration?.productModelConfig?.customFields || [];

    const hasUserData = currentFormValues.tiktokUsername ||
      currentFormValues.amount ||
      currentFormValues.productReference ||
      (currentFormValues.productItems && currentFormValues.productItems.some(item => {
        if (isCustomFormat && customFields.length > 0) {
          return item.customFields && Object.values(item.customFields || {}).some((val: any) => val && val.trim() !== '');
        }
        return item.modelName || item.size;
      })) ||
      // Check personal info to avoid overwriting loaded customer data
      currentFormValues.firstName ||
      currentFormValues.lastName ||
      currentFormValues.address ||
      currentFormValues.phone ||
      currentFormValues.country;

    if (hasUserData) {
      console.log('📝 Données utilisateur détectées, PAS de reset du formulaire');
      console.log('  - tiktokUsername:', currentFormValues.tiktokUsername);
      console.log('  - amount:', currentFormValues.amount);
      console.log('  - productReference:', currentFormValues.productReference);
      console.log('  - productItems:', currentFormValues.productItems);

      // FIX: S'assurer que le pays est synchronisé s'il est manquant dans le formulaire mais présent dans orderData
      // Cela gère le cas où useForm init aurait pu le manquer ou s'il a été ajouté plus tard
      const currentCountry = form.getValues('country');
      if (!currentCountry && orderData.country) {
        console.log('🌍 Patching country from orderData:', orderData.country);
        form.setValue('country', orderData.country);
        // Le useEffect de synchronisation s'occupera de mettre à jour countryDisplayName
      }

      return; // Sortir sans faire de reset pour préserver les données utilisateur
    }

    // Si ce n'est pas une commande fusionnée ET qu'il n'y a pas de données utilisateur, reset normal
    console.log('📝 Reset normal du formulaire (pas de commande fusionnée, pas de données utilisateur)');
    form.reset({
      tiktokUsername: orderData.tiktokUsername || '',
      amount: orderData.amount || '',
      productReference: orderData.productReference || '',
      productItems: orderData.productItems && orderData.productItems.length > 0
        ? orderData.productItems
        : parseProductModelToItems(orderData.productModel),
      email: currentUser?.email || orderData.email || '',
      lastName: orderData.lastName || '',
      firstName: orderData.firstName || '',
      phone: orderData.phone || '',
      address: orderData.address || '',
      houseNumber: orderData.houseNumber || '',
      city: orderData.city || '',
      postalCode: orderData.postalCode || '',
      country: orderData.country || null,
      deliveryMethod: orderData.deliveryMethod || null,
    });

    // Set country display name if country is present
    if (orderData.country) {
      const label = regionNames.of(orderData.country) || orderData.country;
      setCountryDisplayName(label as string);
    } else {
      setCountryDisplayName('');
    }
  }, [orderData, currentUser, form, isGlowLive, isMergedOrder, regionNames]);

  // Sync countryDisplayName with form's country value
  useEffect(() => {
    // Initialize on mount with current value
    const currentCountry = form.getValues('country');
    if (currentCountry) {
      const label = regionNames.of(currentCountry) || currentCountry;
      setCountryDisplayName(label as string);
    }

    // Watch for changes
    const subscription = form.watch((value, { name }) => {
      if (name === 'country' && value.country) {
        const countryCode = value.country as string;
        const label = regionNames.of(countryCode) || countryCode;
        setCountryDisplayName(label as string);
        console.log('🌍 Pays détecté:', label); // Seul log vraiment utile
      }
    });
    return () => subscription.unsubscribe();
  }, [form, regionNames]);

  // Trigger form validation on mount and when orderData changes
  // This ensures that when coming back from payment recap, the button is correctly enabled
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔄 Triggering form validation...');
      form.trigger();
    }, 500); // Small delay to let other effects settle
    return () => clearTimeout(timer);
  }, [form, orderData.email, orderData.address]);

  // Load customer saved information when user is logged in
  useEffect(() => {
    const loadCustomerInfo = async () => {
      // Don't load if already loaded, no user, or if it's a merged order
      if (hasLoadedCustomerInfo.current || !currentUser?.email || isMergedOrder) {
        return;
      }

      // Don't load if form already has data (from orderData or merged order)
      const currentValues = form.getValues();
      const hasExistingData = currentValues.firstName || currentValues.lastName ||
        currentValues.phone || currentValues.address;

      if (hasExistingData) {
        console.log('ℹ️ Le formulaire contient déjà des données, pas de chargement du customer');
        return;
      }

      try {
        console.log('🔍 Chargement des informations sauvegardées du customer...');
        hasLoadedCustomerInfo.current = true; // Mark as loaded

        // Import CustomerService dynamically
        const { CustomerService } = await import('@/services');

        // Find customer by email
        const customer = await CustomerService.findOrCreateCustomer(
          currentUser.email,
          currentUser.uid
        );

        // Check if customer has saved personal information
        if (customer && (customer.firstName || customer.lastName || customer.phone || customer.address)) {
          console.log('✅ Informations du customer trouvées, pré-remplissage du formulaire...');

          // Pre-fill form fields with saved customer data
          if (customer.firstName) {
            form.setValue('firstName', customer.firstName, { shouldValidate: true });
          }

          if (customer.lastName) {
            form.setValue('lastName', customer.lastName, { shouldValidate: true });
          }

          if (customer.tiktokUsername) {
            form.setValue('tiktokUsername', customer.tiktokUsername, { shouldValidate: true });
          }

          if (customer.phone) {
            form.setValue('phone', customer.phone, { shouldValidate: true });
          }

          if (customer.address) {
            form.setValue('address', customer.address, { shouldValidate: true });
          }

          if (customer.houseNumber) {
            form.setValue('houseNumber', customer.houseNumber, { shouldValidate: true });
          }

          if (customer.city) {
            form.setValue('city', customer.city, { shouldValidate: true });
          }

          if (customer.postalCode) {
            form.setValue('postalCode', customer.postalCode, { shouldValidate: true });
          }

          if (customer.country) {
            form.setValue('country', customer.country, { shouldValidate: true });
            const label = regionNames.of(customer.country) || customer.country;
            setCountryDisplayName(label as string);
          }

          // Auto-check the "remember my info" checkbox if info is already saved
          setRememberMyInfo(true);

          // Les champs seront affichés sous forme de carte si l'adresse est complète

          console.log('✅ Formulaire pré-rempli avec les informations du customer');
        } else {
          console.log('ℹ️ Aucune information sauvegardée trouvée pour ce customer');
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des informations du customer:', error);
        // Don't block the form if loading fails
      }
    };

    loadCustomerInfo();
  }, [currentUser?.email, isMergedOrder]); // Only depend on email and merged order status


  // Email validation regex shared constant
  const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  // Fonction pour valider l'email
  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim().length === 0) {
      return false;
    }
    return EMAIL_REGEX.test(email.trim());
  };

  // Vérifier si c'est une commande fusionnée avec debounce
  useEffect(() => {
    const email = form.watch("email");

    // Vérifier si l'email est valide avant de faire la recherche
    if (!email || !isValidEmail(email)) {
      // Ne pas réinitialiser l'état de commande fusionnée si l'email devient invalide
      // Garder les détails existants pour éviter de perdre les informations
      /* console.log('📧 Email invalide, pas de recherche de commande fusionnée'); */
      return;
    }

    // Vérifier si l'email a vraiment changé par rapport au dernier email valide
    if (email === lastValidEmail) {
      /* console.log('📧 Email identique au dernier email valide, pas de recherche'); */
      return;
    }

    // Debounce pour éviter les vérifications trop fréquentes
    // Attendre que l'utilisateur ait fini de taper (1.5 secondes)
    const timeoutId = setTimeout(async () => {
      // Vérifier à nouveau que l'email est toujours valide avant la recherche
      if (!isValidEmail(email)) {
        return;
      }

      // Vérifier à nouveau si l'email a changé pendant le debounce
      if (email === lastValidEmail) {
        return;
      }

      try {
        console.log('🔍 Vérification de commande fusionnée pour l\'email:', email);

        // Mettre à jour le dernier email valide
        setLastValidEmail(email);

        const { order: existingOrder } = await FirebaseOrderService.findMergeableOrderByEmail(
          email,
          configuration?.shippingConfig?.orderMergeConfig,
        );

        // Ne proposer la fusion que si on est dans la période (snapshot) ou le même jour (commande legacy)
        let considerForMerge = existingOrder;
        const snap = existingOrder?.mergeConfigSnapshot;
        const now = new Date();
        if (snap?.periodStart != null && snap?.periodEnd != null) {
          const periodStart = new Date(snap.periodStart);
          const periodEnd = new Date(snap.periodEnd);
          if (now < periodStart || now > periodEnd) {
            considerForMerge = null;
            console.log('📅 Hors période de fusion (now < periodStart ou now > periodEnd), pas de fusion proposée');
          }
        } else {
          // Commande legacy (sans mergeConfigSnapshot) : fusion uniquement si même jour
          const orderTs = existingOrder?.lastProductCreatedAt ?? existingOrder?.createdAt;
          const orderDate = orderTs
            ? (typeof orderTs.toDate === 'function' ? orderTs.toDate() : new Date(orderTs as string | number))
            : null;
          if (orderDate) {
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
            const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            if (orderDate < todayStart || orderDate > todayEnd) {
              considerForMerge = null;
              console.log('📅 Commande legacy pas du même jour, pas de fusion proposée');
            }
          }
        }

        if (considerForMerge && considerForMerge.deliveryInfo.paymentStatus === 'paid') {
          console.log('✅ Commande fusionnée détectée:', existingOrder.id);

          // Déduction de pays supprimée pour éviter les heuristiques en dur

          // Mettre à jour le contexte avec les données de la commande existante
          const detectedCountry = existingOrder.deliveryInfo.country || '';

          // Extract TikTok username from existing order (first product)
          const existingTiktokUsername = existingOrder.products && existingOrder.products.length > 0
            ? existingOrder.products[0].tiktokUsername
            : '';
          console.log('📱 TikTok Username trouvé dans la commande existante:', existingTiktokUsername);

          // Lire les valeurs actuelles du formulaire AVANT de mettre à jour orderData
          const currentFormData = form.getValues();
          console.log('📝 Valeurs du formulaire AVANT mise à jour orderData:', {
            tiktokUsername: currentFormData.tiktokUsername,
            productReference: currentFormData.productReference,
            productItems: currentFormData.productItems
          });

          // Mettre à jour le contexte avec les données de la commande existante
          // IMPORTANT: Préserver les champs de produit existants du formulaire
          setOrderData(prevData => {
            // Utiliser les valeurs du formulaire si disponibles, sinon commande existante, sinon prevData
            const preservedTiktokUsername = currentFormData.tiktokUsername || existingTiktokUsername || prevData.tiktokUsername || '';
            const preservedAmount = currentFormData.amount || prevData.amount || '';
            const preservedProductReference = currentFormData.productReference || prevData.productReference || '';
            const preservedProductItems = currentFormData.productItems || [];

            // Construct productModel from preserved items
            let preservedProductModel = '';
            const modelFormatForPreserve = configuration?.personalization?.productModelConfig?.format || configuration?.productModelConfig?.format || 'structured';
            const isCustomFormatForPreserve = modelFormatForPreserve === 'custom';
            const customFieldsForPreserve = configuration?.personalization?.productModelConfig?.customFields || configuration?.productModelConfig?.customFields || [];

            const hasPreservedItems = preservedProductItems.length > 0 && preservedProductItems.some(item => {
              if (isCustomFormatForPreserve && customFieldsForPreserve.length > 0) {
                return item.customFields && Object.values(item.customFields || {}).some((val: any) => val && val.trim() !== '');
              }
              return item.modelName || item.size;
            });

            if (hasPreservedItems) {
              if (isCustomFormatForPreserve && customFieldsForPreserve.length > 0) {
                // Format custom : utiliser les valeurs des customFields
                preservedProductModel = preservedProductItems
                  .map(item => {
                    const values = customFieldsForPreserve
                      .map((field: any) => {
                        const fieldKey = field.key || `field_${customFieldsForPreserve.indexOf(field)}`;
                        return item.customFields?.[fieldKey]?.trim();
                      })
                      .filter(Boolean);
                    return values.join(' - ').trim();
                  })
                  .filter(Boolean)
                  .join(' + ');
              } else {
                preservedProductModel = preservedProductItems
                  .map(item => [item.modelName, item.size].filter(Boolean).join('-').trim())
                  .filter(Boolean)
                  .join(' + ');
              }
            } else {
              preservedProductModel = prevData.productModel || '';
            }

            console.log('📦 Préservation des champs de produit dans orderData:', {
              tiktokUsername: preservedTiktokUsername,
              amount: preservedAmount,
              productReference: preservedProductReference,
              productModel: preservedProductModel
            });

            return {
              ...prevData,
              email: email,
              // Charger les informations personnelles existantes
              firstName: existingOrder.orderInfo?.firstName || '',
              lastName: existingOrder.orderInfo?.lastName || '',
              phone: existingOrder.orderInfo?.phone || '',
              // Charger les informations de livraison existantes
              address: existingOrder.deliveryInfo.address || '',
              city: existingOrder.deliveryInfo.city || '',
              postalCode: existingOrder.deliveryInfo.postalCode || '',
              country: existingOrder.deliveryInfo.country || detectedCountry,
              deliveryMethod: resolveShippingMethodConfigId(
                existingOrder.deliveryInfo as Record<string, unknown>,
              ),
              shippingCost: existingOrder.deliveryInfo.shippingCost || 0,
              products: existingOrder.products,
              // PRÉSERVER les champs de produit existants (priorité: formulaire > prevData)
              tiktokUsername: preservedTiktokUsername,
              amount: preservedAmount,
              productReference: preservedProductReference,
              productModel: preservedProductModel,
              productItems: preservedProductItems,
            };
          });

          // Mettre à jour le formulaire avec les données de la commande existante
          // MAIS préserver les champs de produit déjà saisis par l'utilisateur
          console.log('📝 Données actuelles du formulaire avant fusion (après mise à jour orderData):', currentFormData);

          // Remplir le formulaire avec les données de la commande existante
          form.setValue('email', email, { shouldValidate: true });
          form.setValue('firstName', existingOrder.orderInfo?.firstName || '', { shouldValidate: true });
          form.setValue('lastName', existingOrder.orderInfo?.lastName || '', { shouldValidate: true });
          form.setValue('phone', existingOrder.orderInfo?.phone || '', { shouldValidate: true });
          form.setValue('address', existingOrder.deliveryInfo.address || '', { shouldValidate: true });
          form.setValue('city', existingOrder.deliveryInfo.city || '', { shouldValidate: true });
          form.setValue('postalCode', existingOrder.deliveryInfo.postalCode || '', { shouldValidate: true });
          // Le houseNumber est important pour la validation
          form.setValue('houseNumber', existingOrder.deliveryInfo.houseNumber || '', { shouldValidate: true });
          const mergedCountry = existingOrder.deliveryInfo.country || detectedCountry || null;
          form.setValue('country', mergedCountry as any, { shouldValidate: true });

          // Mettre également à jour le pays dans orderData pour la cohérence
          setOrderData(prev => ({ ...prev, country: mergedCountry as any }));

          const countryLabel = mergedCountry ? (regionNames.of(mergedCountry as string) || (mergedCountry as string)) : '';
          setCountryDisplayName(countryLabel as string);
          form.setValue(
            'deliveryMethod',
            resolveShippingMethodConfigId(existingOrder.deliveryInfo as Record<string, unknown>),
            { shouldValidate: true },
          );

          // NE PAS écraser les champs de produit si l'utilisateur les a déjà saisis
          // Seulement les définir si ils sont vides
          console.log('📝 Préservation des champs de produit:');
          console.log('  - tiktokUsername actuel:', currentFormData.tiktokUsername);
          console.log('  - amount actuel:', currentFormData.amount);
          console.log('  - productReference actuel:', currentFormData.productReference);
          console.log('  - productItems actuel:', currentFormData.productItems);

          if (!currentFormData.tiktokUsername) {
            form.setValue('tiktokUsername', existingTiktokUsername || '');
            console.log('  - tiktokUsername défini à partir de la commande existante:', existingTiktokUsername);
          } else {
            console.log('  - tiktokUsername préservé:', currentFormData.tiktokUsername);
          }
          if (!currentFormData.amount) {
            form.setValue('amount', '');
            console.log('  - amount défini à vide');
          } else {
            console.log('  - amount préservé:', currentFormData.amount);
          }
          if (!currentFormData.productReference) {
            form.setValue('productReference', '');
            console.log('  - productReference défini à vide');
          } else {
            console.log('  - productReference préservé:', currentFormData.productReference);
          }
          // Check if items are empty
          const modelFormat = configuration?.personalization?.productModelConfig?.format || configuration?.productModelConfig?.format || 'structured';
          const isCustomFormat = modelFormat === 'custom';
          const customFields = configuration?.personalization?.productModelConfig?.customFields || configuration?.productModelConfig?.customFields || [];

          const hasItems = currentFormData.productItems && currentFormData.productItems.some(item => {
            if (isCustomFormat && customFields.length > 0) {
              // Pour le format custom, vérifier si au moins un customField a une valeur
              return item.customFields && Object.values(item.customFields).some((val: any) => val && val.trim() !== '');
            }
            return item.modelName || item.size;
          });

          if (!hasItems) {
            if (isCustomFormat && customFields.length > 0) {
              // Initialiser avec la structure customFields
              const newItem: any = {};
              customFields.forEach((customField: any) => {
                const fieldKey = customField.key || `field_${customFields.indexOf(customField)}`;
                newItem.customFields = newItem.customFields || {};
                newItem.customFields[fieldKey] = '';
              });
              form.setValue('productItems', [newItem]);
              console.log('  - productItems réinitialisé (format custom)');
            } else {
              form.setValue('productItems', [{ modelName: '', size: '' }]);
              console.log('  - productItems réinitialisé (vide)');
            }
          } else {
            console.log('  - productItems préservé');
          }

          // Définir le point relais s'il existe
          if (existingOrder.deliveryInfo.pickupPoint) {
            setSelectedPickupPoint(existingOrder.deliveryInfo.pickupPoint);
          }

          // Marquer comme commande fusionnée
          setIsMergedOrder(true);

          toast.success(t('orderForm.messages.existingOrderFound'));
        } else {
          console.log('❌ Aucune commande fusionnée trouvée pour l\'email:', email);
          // Toujours réinitialiser l'état de commande fusionnée si aucune commande n'est trouvée
          // Cela garantit que le formulaire repasse en mode "nouvelle commande"
          console.log('🔄 Aucune commande fusionnée trouvée, réinitialisation de l\'état');
          setIsMergedOrder(false);
          // Réinitialiser aussi le point relais si on passe d'une commande fusionnée à une nouvelle
          if (selectedPickupPoint) {
            setSelectedPickupPoint(null);
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors de la vérification de commande fusionnée:', error);
        // Ne pas réinitialiser l'état en cas d'erreur non plus
        console.log('⚠️ Erreur lors de la recherche, garde l\'état actuel');
      }
    }, 1500); // Délai de 1.5 secondes après que l'utilisateur ait arrêté de taper

    return () => clearTimeout(timeoutId);
  }, [watchedEmail, configuration?.shippingConfig?.orderMergeConfig]);

  // Use dynamic configuration from Firebase
  const brandInfo = useMemo(() => {
    if (loading) {
      return {
        logo: null,
        name: t('orderForm.messages.loading'),
        subtitle: t('orderForm.messages.defaultSubtitle')
      };
    }

    if (!configuration) {
      return {
        logo: null,
        name: t('orderForm.messages.defaultOrganization'),
        subtitle: t('orderForm.messages.defaultSubtitle')
      };
    }

    return {
      logo: getOrganizationLogoUrl(configuration) || null,
      name: configuration.name || t('orderForm.messages.defaultOrganization'),
      subtitle:
        configuration.personalization?.description ||
        configuration.description ||
        t('orderForm.messages.defaultSubtitle'),
    };
  }, [configuration, loading]);


  // Function to get user's location and postal code
  const getUserLocation = async () => {
    console.log('🗺️ Démarrage de la géolocalisation...');
    setIsGettingLocation(true);
    try {
      if (!navigator.geolocation) {
        toast.error(t('orderForm.errors.geolocationNotSupported'));
        return;
      }

      console.log('📍 Demande de position...');
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        });
      });

      const { latitude, longitude } = position.coords;
      console.log('📍 Position obtenue:', { latitude, longitude });

      // Use a free geocoding service as alternative
      console.log('🌐 Appel à l\'API de géocodage alternative...');

      try {
        // Try with a free service first
        const response = await fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=fr`
        );
        const data = await response.json();

        console.log('🌐 Réponse de l\'API BigDataCloud:', data);

        if (data.postcode) {
          const postalCode = data.postcode;
          console.log('🗺️ Code postal détecté:', postalCode);

          // Update state and input directly
          setPickupSearchPostalCode(postalCode);

          // Also update the input directly as fallback
          if (postalCodeInputRef.current) {
            postalCodeInputRef.current.value = postalCode;
            console.log('📝 Input mis à jour directement avec:', postalCode);
          }

          // Auto-search pickup points with detected postal code
          await searchPickupPointsByLocation(postalCode);
        } else {
          console.log('❌ Aucun code postal trouvé dans la réponse');
          toast.error(t('orderForm.errors.postalCodeError'));
        }
      } catch (geocodeError) {
        console.log('❌ Erreur avec l\'API alternative, essai avec Google...');

        // Fallback to Google API (may need API key configuration)
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const data = await response.json();

          if (data.error_message) {
            console.log('❌ Erreur Google API:', data.error_message);
            toast.error(t('orderForm.errors.geolocationServiceUnavailable'));
            return;
          }

          if (data.results && data.results.length > 0) {
            const addressComponents = data.results[0].address_components;
            const postalCodeComponent = addressComponents.find((component: any) =>
              component.types.includes('postal_code')
            );

            if (postalCodeComponent) {
              const postalCode = postalCodeComponent.long_name;
              setPickupSearchPostalCode(postalCode);

              if (postalCodeInputRef.current) {
                postalCodeInputRef.current.value = postalCode;
              }

              await searchPickupPointsByLocation(postalCode);
            }
          }
        } catch (googleError) {
          console.log('❌ Erreur Google API:', googleError);
          toast.error(t('orderForm.errors.postalCodeErrorManual'));
        }
      }
    } catch (_error) {
      console.error('Erreur de géolocalisation:', _error);
      toast.error(t('orderForm.errors.geolocationError'));
    } finally {
      setIsGettingLocation(false);
    }
  };

  // Function to get postal code from city name
  const getPostalCodeFromCity = async (cityName: string): Promise<string | null> => {
    try {
      const selectedCountry = form.watch("country") || 'FR';
      console.log('🏙️ Recherche du code postal pour la ville:', cityName, 'dans le pays:', selectedCountry);

      // Use BigDataCloud free API to get postal code from city
      const response = await fetch(
        `https://api.bigdatacloud.net/data/city?name=${encodeURIComponent(cityName)}&countryId=${selectedCountry}&localityLanguage=fr`
      );

      if (response.ok) {
        const data = await response.json();
        console.log('🌐 Réponse API ville:', data);

        if (data.postcode) {
          return data.postcode;
        }
      }

      // Fallback: try with Google Geocoding if available
      try {
        const geocodeResponse = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(cityName + ', France')}&key=${GOOGLE_MAPS_API_KEY}`
        );
        const geocodeData = await geocodeResponse.json();

        if (geocodeData.results && geocodeData.results.length > 0) {
          const postalCodeComponent = geocodeData.results[0].address_components.find((component: any) =>
            component.types.includes('postal_code')
          );

          if (postalCodeComponent) {
            return postalCodeComponent.long_name;
          }
        }
      } catch (googleError) {
        console.log('❌ Erreur Google Geocoding:', googleError);
      }

      return null;
    } catch (_error) {
      console.error('❌ Erreur lors de la recherche du code postal:', _error);
      return null;
    }
  };

  // Function to search for pickup points by postal code or city
  const searchPickupPointsByLocation = async (searchValue?: string) => {
    const searchTerm = searchValue || pickupSearchPostalCode;

    if (!searchTerm || searchTerm.length < 3) {
      toast.error(t('orderForm.errors.pickupSearchMinChars'));
      return;
    }

    // Désélectionner le point relais précédemment sélectionné car les résultats vont changer
   
    setSelectedPickupPoint(null);
    setContextSelectedPickupPoint(null);
    form.setValue('pickupPoint', undefined, { shouldValidate: true });
    // Vider aussi orderData.pickupPoint pour que l'effet de restauration ne réapplique pas l'ancien point
    setOrderData((prev) => ({ ...prev, pickupPoint: undefined }));

    setLoadingPickupPoints(true);
    setHasSearchedPickupPoints(true);
    try {
      // Detect if it's a postal code (numbers only) or city name
      const isPostalCode = /^\d+$/.test(searchTerm);

      let postalCodeToSearch = '';

      if (isPostalCode) {
        console.log('🔍 Recherche directe par code postal:', searchTerm);
        postalCodeToSearch = searchTerm;
      } else {
        console.log('🔍 Recherche du code postal pour la ville:', searchTerm);

        const foundPostalCode = await getPostalCodeFromCity(searchTerm);
        if (foundPostalCode) {
          postalCodeToSearch = foundPostalCode;
          console.log('✅ Code postal trouvé pour', searchTerm, ':', foundPostalCode);
        } else {
          toast.error(t('orderForm.errors.postalCodeNotFound', { city: searchTerm }));
          setHasSearchedPickupPoints(false);
          return;
        }
      }

      // Search pickup points with postal code
      const selectedCountry = form.watch("country");

      const selectedDeliveryMethod = form.watch("deliveryMethod");
      const selectedMethod = getShippingMethodById(selectedDeliveryMethod || '');

      if (!selectedMethod?.id || !organizationId) {
        toast.error(t('orderForm.errors.pickupSearchError'));
        setHasSearchedPickupPoints(false);
        return;
      }

      console.log('📦 Recherche des points relais:', {
        postalCode: postalCodeToSearch,
        country: selectedCountry,
        provider: selectedMethod.provider,
        methodId: selectedMethod.id,
      });

      const points = await PickupService.fetchPickupPoints(
        postalCodeToSearch,
        organizationId,
        selectedMethod.id,
        selectedCountry || 'FR'
      );

      setPickupPoints(points);
      if (points.length === 0) {
        toast.error(t('orderForm.errors.pickupNotFound'));
      }
    } catch (_error) {
      console.error('Erreur lors de la recherche des points relais:', _error);
      toast.error(t('orderForm.errors.pickupSearchError'));
    } finally {
      setLoadingPickupPoints(false);
    }
  };

  // Handle form errors - Scroll to the first error
  const onInvalid = (errors: any) => {
    console.log('🚨 Form Errors:', errors);
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      // Priorité d'affichage pour le scroll
      const priorityOrder = [
        'amount',
        'productReference',
        'tiktokUsername',
        'email',
        'lastName',
        'firstName',
        'phone',
        'address',
        'houseNumber',
        'city',
        'postalCode',
        'country',
        'deliveryMethod'
      ];

      const firstErrorKey = priorityOrder.find(key => errors[key]) || errorKeys[0];

      // Mapping des clés vers les IDs DOM
      const keyToId: Record<string, string> = {
        amount: 'amount',
        productReference: 'productReference',
        tiktokUsername: 'tiktokUsername',
        email: 'email',
        lastName: 'lastName',
        firstName: 'firstName',
        phone: 'phone-input-container',
        address: 'address-input-google',
        houseNumber: 'houseNumber',
        city: 'city',
        postalCode: 'postalCode',
        country: 'country',
        deliveryMethod: 'shipping-methods-container'
      };

      const elementId = keyToId[firstErrorKey] || firstErrorKey;
      const element = document.getElementById(elementId);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Mettre le focus si possible sur un input à l'intérieur
        const focusable = (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')
          ? element
          : element.querySelector('input:not([type="hidden"]), textarea, select');
        if (focusable) (focusable as HTMLElement).focus({ preventScroll: true });
      }
    }
  };


  // Handle form submission
  const onSubmit = async (data: UnifiedFormData) => {
    try {
      // Détecter le format depuis la configuration
      const modelFormat = configuration?.personalization?.productModelConfig?.format || configuration?.productModelConfig?.format || 'structured';
      const isFreeFormat = modelFormat === 'free';
      const isCustomFormat = modelFormat === 'custom';
      const customFields = configuration?.personalization?.productModelConfig?.customFields || configuration?.productModelConfig?.customFields || [];

      // Construire productModel selon le format
      const productModel = isFreeFormat
        ? // Format libre : utiliser uniquement modelName
        data.productItems
          .map(item => item.modelName?.trim())
          .filter(Boolean)
          .join(' + ')
        : isCustomFormat && customFields.length > 0
          ? // Format custom : utiliser les valeurs des customFields
          data.productItems
            .map(item => {
              const values = customFields
                .map((field: any) => {
                  const fieldKey = field.key || `field_${customFields.indexOf(field)}`;
                  return item.customFields?.[fieldKey]?.trim();
                })
                .filter(Boolean);
              return values.join(' - ').trim();
            })
            .filter(Boolean)
            .join(' + ')
          : // Format structuré : concaténer modelName et size
          data.productItems
            .map(item => [item.modelName, item.size].filter(Boolean).join('-').trim())
            .filter(Boolean)
            .join(' + ');

      console.log('📝 ========================================');
      console.log('📝 FORM SUBMISSION DEBUG');
      console.log('📝 ========================================');
      console.log('🔍 Creating order with all data:', data);
      console.log('📝 Form data details:');
      console.log('  - firstName:', data.firstName);
      console.log('  - lastName:', data.lastName);
      console.log('  - email:', data.email);
      console.log('  - phone:', data.phone);
      console.log('  - address:', data.address);
      console.log('  - city:', data.city);
      console.log('  - postalCode:', data.postalCode);
      console.log('  - country:', data.country);
      console.log('  - deliveryMethod:', data.deliveryMethod);
      console.log('📝 ========================================');

      // Get shipping method details and calculate cost
      const selectedShippingMethod = getShippingMethodById(data.deliveryMethod || '');

      console.log('📦 Selected shipping method:', selectedShippingMethod);
      if (selectedShippingMethod) {
        console.log('📦 Shipping method details:');
        console.log('  - id:', selectedShippingMethod.id);
        console.log('  - name:', selectedShippingMethod.name);
        console.log('  - price:', selectedShippingMethod.price);
        console.log('  - provider:', selectedShippingMethod.provider);
        console.log('  - sendcloudMethodId:', selectedShippingMethod.sendcloudMethodId);
        console.log('  - estimatedHours:', selectedShippingMethod.estimatedHours);
        console.log('  - minWeight:', selectedShippingMethod.minWeight);
        console.log('  - maxWeight:', selectedShippingMethod.maxWeight);
        console.log('  - isHomeDelivery:', selectedShippingMethod.isHomeDelivery);
      }

      // VALIDATION STRICTE : uniquement pour les NOUVELLES commandes (pas pour les commandes fusionnées)
      if (!isMergedOrder) {
        // Vérifier que houseNumber est présent
        const houseNumber = form.watch("houseNumber");
        if (!houseNumber || houseNumber.trim() === '') {
          form.setError("address", {
            type: "manual",
            message: t('orderForm.steps.shippingInfo.address.missingHouseNumberSubmit')
          });
          // Scroll vers le champ d'erreur
          const addressInput = document.getElementById("address");
          if (addressInput) {
            addressInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }

        // Vérifier si un point relais est requis mais non sélectionné
        // Utiliser isHomeDelivery depuis la configuration
        const isPickupMethod = selectedShippingMethod && !selectedShippingMethod.isHomeDelivery;

        if (isPickupMethod && !data.pickupPoint) {
          toast.error(t('orderForm.errors.pickupPointRequired'));
          return;
        }
        if (
          isPickupMethod &&
          data.pickupPoint &&
          String(selectedShippingMethod?.provider || '').toLowerCase() === 'mondial_relay'
        ) {
          const relayCode = typeof data.pickupPoint.code === 'string' ? data.pickupPoint.code.trim() : '';
          if (!relayCode) {
            toast.error(
              'Point relais Mondial Relay : le code du point (ex. FR35439) est manquant. Veuillez relancer une recherche et sélectionner un relais.',
            );
            return;
          }
        }
      }

      console.log('📍 Selected pickup point:', data.pickupPoint);
      if (data.pickupPoint) {
        console.log('📍 Pickup point details:');
        console.log('  - id:', data.pickupPoint.id);
        console.log('  - name:', data.pickupPoint.name);
        console.log('  - address:', data.pickupPoint.address);
        console.log('  - city:', data.pickupPoint.city);
        console.log('  - postalCode:', data.pickupPoint.postalCode);
        console.log('  - country:', data.pickupPoint.country);
        console.log('  - provider:', data.pickupPoint.provider);
      }

      const shippingCost = selectedShippingMethod
        ? (data.country ? selectedShippingMethod.getPriceForCountry(data.country || '') : selectedShippingMethod.price)
        : 0;

      console.log('💰 Shipping cost calculated:', shippingCost);
      console.log('🌍 Selected country:', data.country);
      console.log('🏢 Organization ID:', organizationId);
      console.log('🔄 Is merged order:', isMergedOrder);

      const finalAddress = data.address || '';

      // Create complete order data including pickup point
      // Ensure no null values are passed where undefined is expected
      const completeOrderData: OrderData = {
        organizationName: configuration?.name || undefined,
        ...data,
        address: data.address || '',
        fullAddress: data.fullAddress || undefined,
        city: data.city || '',
        postalCode: data.postalCode || '',
        country: data.country || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        productModel: productModel, // Use concatenated string
        shippingCost: shippingCost,
        // Include pickup point in order data
        pickupPoint: data.pickupPoint,
        productItems: data.productItems,
      };

      console.log('📦 Complete order data:', completeOrderData);

      // Enregistrer / fusionner la commande (étape 1 logique)
      const result = await saveOrder(completeOrderData);
      console.log('📋 Order save result:', result);
      console.log('📋 Order save result.orderId:', result.orderId);
      console.log('📋 Order save result.isMerged:', result.isMerged);

      if (result.isMerged) {
        console.log('🔄 Order was merged, navigating directly to payment...');
        const mergedOrderId = result.orderId || currentOrderId;
        if (mergedOrderId) {
          try {
            await FirebaseOrderService.ensureShippingConfigIdsOnOrder(mergedOrderId);
          } catch (e) {
            console.warn('ensureShippingConfigIdsOnOrder (merged):', e);
          }
        }
        toast.success(t('orderForm.messages.orderMerged'));
        navigateToPayment();
      } else {
        // Try to use orderId from result first, fallback to currentOrderId
        const orderId = result.orderId || currentOrderId;

        if (!orderId) {
          console.error('❌ No orderId available in result or context');
          console.error('❌ result.orderId:', result.orderId);
          console.error('❌ currentOrderId:', currentOrderId);
          toast.error(t('orderForm.errors.orderCreationError'));
          return;
        }

        console.log('📝 Order ID from result:', result.orderId);
        console.log('📝 Order ID from context:', currentOrderId);
        console.log('📝 Using orderId:', orderId);

        // Update delivery info using orderId from result
        if (orderId) {
          // Extraire toutes les informations de la méthode de livraison sélectionnée
          const rawMethodKey = selectedShippingMethod?.id;
          const parsedNum =
            rawMethodKey != null && /^\d+$/.test(String(rawMethodKey).trim())
              ? Number(rawMethodKey)
              : NaN;
          const methodId = Number.isFinite(parsedNum) ? parsedNum : undefined;
          const methodName = selectedShippingMethod?.name;
          const methodDescription = selectedShippingMethod?.description;
          const provider = selectedShippingMethod?.provider || 'sendcloud'; // Par défaut sendcloud
          const estimatedHours = selectedShippingMethod?.estimatedHours;
          const minWeight = selectedShippingMethod?.minWeight ? Number(selectedShippingMethod.minWeight) : undefined;
          const maxWeight = selectedShippingMethod?.maxWeight ? Number(selectedShippingMethod.maxWeight) : undefined;
          const servicePointInput = selectedShippingMethod?.servicePointInput;
          const isHomeDelivery = selectedShippingMethod?.isHomeDelivery ?? undefined;

          // Informations complètes de la méthode de livraison
          // methodId est déjà défini plus haut
          const methodPrice = selectedShippingMethod?.price;
          const methodCurrency = 'EUR'; // Devise par défaut
          const methodStatus = 'active'; // Statut par défaut

          // Calculer le poids total des produits (ou utiliser un poids par défaut)
          const totalWeight = 0.5; // Poids par défaut pour les tests

          console.log('🚚 ========================================');
          console.log('🚚 DELIVERY INFO DEBUG');
          console.log('🚚 ========================================');
          console.log(`📦 Mise à jour livraison complète - Méthode: ${methodId}, Nom: ${methodName}, Poids: ${totalWeight} kg`);
          console.log('🚚 Delivery info parameters:');
          console.log('  - orderId:', orderId);
          console.log('  - deliveryMethod:', data.deliveryMethod);
          console.log('  - shippingCost:', completeOrderData.shippingCost);
          console.log('  - address:', data.address);
          console.log('  - city:', data.city);
          console.log('  - postalCode:', data.postalCode);
          console.log('  - country:', data.country);
          console.log('  - pickupPoint:', data.pickupPoint);
          console.log('  - methodId:', methodId);
          console.log('  - provider:', provider);
          console.log('  - weight:', totalWeight);
          console.log('  - methodName:', methodName);
          console.log('  - methodDescription:', methodDescription);
          console.log('  - estimatedHours:', estimatedHours);
          console.log('  - minWeight:', minWeight);
          console.log('  - maxWeight:', maxWeight);
          console.log('  - servicePointInput:', servicePointInput);
          console.log('  - isHomeDelivery:', isHomeDelivery);
          console.log('  - methodPrice:', methodPrice);
          console.log('  - methodCurrency:', methodCurrency);
          console.log('  - methodStatus:', methodStatus);
          console.log('🚚 ========================================');

          await FirebaseOrderService.updateDeliveryInfo({
            orderId: orderId,
            deliveryMethod: data.deliveryMethod,
            shippingCost: completeOrderData.shippingCost,
            address: data.address || '',
            fullAddress: data.fullAddress || undefined,
            houseNumber: data.houseNumber, // OBLIGATOIRE - extrait de Google Autocomplete
            city: data.city,
            postalCode: data.postalCode,
            country: data.country || undefined,
            pickupPoint: data.pickupPoint || undefined,
            methodId,
            provider,
            weight: totalWeight,
            methodName,
            methodDescription,
            estimatedHours,
            minWeight,
            maxWeight,
            servicePointInput,
            isHomeDelivery,
            // Informations complètes de la méthode de livraison
            methodPrice,
            methodCurrency,
            methodStatus
          });

          try {
            await FirebaseOrderService.ensureShippingConfigIdsOnOrder(orderId);
          } catch (e) {
            console.warn('ensureShippingConfigIdsOnOrder:', e);
          }

          console.log('✅ Delivery info updated successfully for order:', orderId);
        } else {
          console.error('❌ Cannot update delivery info: orderId is missing');
        }

        // Update the order context with complete data
        setOrderData(completeOrderData);

        // Si l'utilisateur est connecté et a coché "Se souvenir de mes informations"
        if (currentUser && rememberMyInfo) {
          try {
            console.log('💾 Sauvegarde des informations personnelles du customer...');

            // Trouver ou créer le customer
            const { CustomerService } = await import('@/services');
            const customer = await CustomerService.findOrCreateCustomer(
              data.email,
              currentUser.uid
            );

            // Mettre à jour les informations personnelles
            await CustomerService.updatePersonalInfo(customer.id, {
              firstName: data.firstName,
              lastName: data.lastName,
              tiktokUsername: data.tiktokUsername,
              phone: data.phone,
              address: data.address || '',
              houseNumber: data.houseNumber,
              city: data.city,
              postalCode: data.postalCode,
              country: data.country || undefined,
            });

            console.log('✅ Informations personnelles sauvegardées avec succès');
          } catch (error) {
            console.error('❌ Erreur lors de la sauvegarde des informations personnelles:', error);
            // Ne pas bloquer le processus de commande si la sauvegarde échoue
          }
        }

        // Navigate to payment (recap + payment selection)
        console.log('📦 Order created, navigating to payment...');
        navigateToPayment();
      }
    } catch (_error) {
      console.error('❌ Error in form submission:', _error);
      toast.error(t('orderForm.errors.orderCreationError'));
    }
  };

  // Données structurées pour le SEO
  const structuredData = useMemo(() => {
    if (!configuration) return null;

    return {
      "@context": "https://schema.org",
      "@type": "Store",
      "name": configuration.name,
      "description": configuration.personalization?.description || configuration.description || `Portail de commande pour ${configuration.name}`,
      "image": configuration.personalization?.logo || configuration.logo || "https://cashlive.fr/og-image.jpg",
      "url": window.location.href,
      "priceRange": "€€",
      "telephone": configuration.phone,
      "email": configuration.email,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "FR"
      }
    };
  }, [configuration]);

  // Si changement d'organisation détecté, afficher message de redirection
  if (hasOrganizationChanged) {
    return (
      <div className="min-h-screen py-8">
        <SeoTags
          title="Redirection | CashLive"
          description="Redirection en cours..."
          url={window.location.href}
          noIndex={true}
        />
        <div className="max-w-md mx-auto">
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-16 h-16 text-yellow-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-2xl font-bold text-yellow-800 mb-2">
                {t('orderForm.errors.organizationChanged')}
              </h2>
              <p className="text-yellow-700 text-lg mb-4">
                {t('orderForm.errors.organizationChangedDescription')}
              </p>
              <p className="text-yellow-600 text-sm">
                {t('orderForm.errors.organizationChangedRedirect')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si pas de configuration trouvée, afficher seulement le message d'erreur
  if (!loading && !configuration) {
    return (
      <div className="min-h-screen py-8">
        <SeoTags
          title="Organisation introuvable | CashLive"
          description="Cette organisation n'existe pas ou n'est plus active."
          url={window.location.href}
          noIndex={true}
        />
        <div className="max-w-md mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-16 h-16 text-red-500 mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h2 className="text-2xl font-bold text-red-800 mb-2">
                {t('orderForm.errors.organizationNotFound')}
              </h2>
              <p className="text-red-700 text-lg mb-4">
                {t('orderForm.errors.organizationNotFoundDescription')}
              </p>
              <p className="text-red-600 text-sm">
                {t('orderForm.errors.organizationNotFoundHelp')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fonction pour ouvrir/fermer la sidebar
  const togglePersonalizationSidebar = () => {
    // Vérifier les permissions avant d'ouvrir la sidebar
    if (!canManagePersonalization) {
      toast.error(t('admin.configuration.personalization.noPermission') || 'Vous n\'avez pas les droits pour accéder à la personnalisation');
      return;
    }

    if (isPersonalizationEditMode) {
      searchParams.delete('edit');
      setSearchParams(searchParams);
    } else {
      searchParams.set('edit', 'personalization');
      setSearchParams(searchParams);
    }
  };

  return (
    <>
      {/* Style CSS pour forcer la couleur du texte et du background des boutons primaires dans tous les états */}
      <style>{(() => {
        const isGradient = personalization.button.primary && (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient'));
        const primaryBg = personalization.button.primary
          ? (isGradient
            ? `background: ${personalization.button.primary} !important; background-color: transparent !important;`
            : `background-color: ${personalization.button.primary} !important;`)
          : `background-color: #9333EA !important;`;

        const isHoverGradient = personalization.button.primaryHover && (personalization.button.primaryHover.includes('gradient') || personalization.button.primaryHover.includes('linear-gradient'));
        const hoverBg = personalization.button.primaryHover
          ? (isHoverGradient
            ? `background: ${personalization.button.primaryHover} !important; background-color: transparent !important;`
            : `background-color: ${personalization.button.primaryHover} !important;`)
          : primaryBg;

        return `
          button[data-theme-button="primary"]:not(:disabled) {
            color: ${personalization.button.primaryText || '#FFFFFF'} !important;
            ${primaryBg}
          }
          button[data-theme-button="primary"]:hover:not(:disabled) {
            color: ${personalization.button.primaryText || '#FFFFFF'} !important;
            ${hoverBg}
          }
          button[data-theme-button="primary"]:active:not(:disabled) {
            color: ${personalization.button.primaryText || '#FFFFFF'} !important;
            ${hoverBg}
          }
          button[data-theme-button="primary"]:focus:not(:disabled) {
            color: ${personalization.button.primaryText || '#FFFFFF'} !important;
            outline: 2px solid ${isGradient ? '#9333EA' : (personalization.button.primary || '#9333EA')};
            outline-offset: 2px;
          }
          button[data-theme-button="primary"]:disabled {
            color: ${personalization.button.disabledText || '#6B7280'} !important;
            background-color: ${personalization.button.disabledBackground || '#D1D5DB'} !important;
            cursor: not-allowed !important;
            opacity: 0.6 !important;
          }
        `;
      })()}</style>
      {/* Overlay sombre sur mobile quand sidebar visible */}
      {isPersonalizationEditMode && canManagePersonalization && (
        <div
          className="fixed inset-0 top-24 bg-black bg-opacity-50 z-[55] lg:hidden"
          onClick={togglePersonalizationSidebar}
        />
      )}

      {/* Bouton flottant pour ouvrir/fermer la sidebar sur mobile */}
      {!isPersonalizationEditMode && canManagePersonalization && (
        <button
          onClick={togglePersonalizationSidebar}
          className="fixed bottom-6 right-6 lg:hidden z-[50] p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95"
          style={{
            backgroundColor: personalization.button.primary || '#9333EA',
            color: personalization.button.primaryText || '#FFFFFF',
          }}
          aria-label={t('admin.configuration.personalization.editor.title')}
        >
          <Palette className="w-6 h-6" />
        </button>
      )}

      {/* Sidebar de personnalisation fixe sur la gauche */}
      {isPersonalizationEditMode && organizationId && canManagePersonalization && (
        <PersonalizationEditor organizationId={organizationId} />
      )}

      {/* Contenu principal avec marge gauche si sidebar visible */}
      <div className={`w-full max-w-lg mx-auto space-y-6 pt-6 transition-all duration-300 ${isPersonalizationEditMode ? 'lg:ml-[420px] lg:mr-0' : ''}`}>
        <SeoTags
          title={`${brandInfo.name} | Paiement Sécurisé CashLive`}
          description={brandInfo.subtitle || `Finalisez votre commande chez ${brandInfo.name} en toute sécurité avec CashLive.`}
          image={brandInfo.logo || '/og-image.jpg'}
          url={window.location.href}
          noIndex={true}
          structuredData={structuredData ?? undefined}
        />
        {/* Header Section */}
        <div
          className={`p-4 sm:p-6 md:p-8 overflow-hidden ${getCardStyles().className}`}
          style={{
            backgroundColor: personalization.card.background,
            borderColor: personalization.card.border,
            ...getCardStyles().style
          }}
        >
          <div className="text-center">
            <div className="mb-6">
              {/* Brand Logo */}
              {brandInfo.logo ? (
                <img
                  src={brandInfo.logo}
                  alt={`${brandInfo.name} Logo`}
                  className="w-20 h-20 mx-auto object-contain"
                />
              ) : (
                <div className="w-20 h-20 mx-auto bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            <h1
              className="text-3xl font-bold mb-2"
              style={{ color: personalization.text.heading }}
            >
              {brandInfo.name}
            </h1>
            <div
              className="text-base mb-6 min-w-0"
              style={{ color: personalization.text.secondary }}
            >
              <MarkdownText content={brandInfo.subtitle} />
            </div>


            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 px-2 sm:px-0">
              {(() => {
                const badgeStyles = getBadgeStyles();
                return (
                  <>
                    <span className={badgeStyles.className} style={badgeStyles.style}>
                      <svg className="w-4 h-4 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20" style={{ color: personalization.icon.primary }}>
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('orderForm.badges.verifiedSeller')}
                    </span>
                    <span className={badgeStyles.className} style={badgeStyles.style}>
                      <svg className="w-4 h-4 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20" style={{ color: personalization.icon.primary }}>
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {t('orderForm.badges.securePayment')}
                    </span>
                    <span className={badgeStyles.className} style={badgeStyles.style}>
                      <svg className="w-4 h-4 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20" style={{ color: personalization.icon.primary }}>
                        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                        <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1V8a1 1 0 00-1-1h-3z" />
                      </svg>
                      {t('orderForm.badges.fastDelivery')}
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Annonce informative (Bannière client) */}
        {personalization.informativeAnnouncement && (
          <div
            className={`p-5 mb-6 overflow-hidden relative border transition-all duration-300 ${personalization.card.borderRadius && !personalization.card.borderRadius.includes('#') ? personalization.card.borderRadius : 'rounded-2xl'} ${personalization.card.shadow && !personalization.card.shadow.includes('#') ? personalization.card.shadow : 'shadow-md'}`}
            style={{
              backgroundColor: getColorWithOpacity(personalization.button.primary, 0.03),
              borderColor: getColorWithOpacity(personalization.button.primary, 0.2),
              borderLeftWidth: '6px',
              borderLeftColor: personalization.button.primary || '#9333EA',
            }}
          >
            {/* Petit badge élégant */}
            <div
              className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 shadow-sm"
              style={{
                backgroundColor: personalization.button.primary || '#9333EA',
                color: personalization.button.primaryText || '#FFFFFF'
              }}
            >
              <Sparkles className="w-2.5 h-2.5" />
              Information
            </div>

            <div className="flex items-start gap-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm transition-transform hover:scale-105"
                style={{
                  backgroundColor: getColorWithOpacity(personalization.button.primary, 0.1),
                  color: personalization.button.primary || '#9333EA'
                }}
              >
                <Bell className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0 pr-16 lg:pr-20">
                <h3
                  className="text-base font-bold mb-1.5 flex items-center gap-2"
                  style={{ color: personalization.text.heading }}
                >
                  {t('orderForm.announcement.title') || 'À savoir'}
                </h3>
                <div
                  className="text-[13px] leading-relaxed font-normal opacity-90 min-w-0"
                  style={{ color: personalization.text.secondary }}
                >
                  <MarkdownText content={personalization.informativeAnnouncement} />
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Order Amount Section */}
        <div
          className={`p-8 ${getCardStyles().className}`}
          style={{
            backgroundColor: personalization.card.background,
            borderColor: personalization.card.border,
            ...getCardStyles().style
          }}
        >
          <h2
            className="text-xl font-semibold mb-4 text-center"
            style={{ color: personalization.text.heading }}
          >
            {t('orderForm.sections.orderAmount')}
          </h2>
          <div className="text-center">
            {(() => {
              const inputStyles = getInputStyles();
              const focusStyles = getInputFocusStyles();
              const { onBlur: amountBlur, ...amountRegister } = form.register("amount", {
                required: t('orderForm.validation.amountRequired'),
                min: { value: 0.01, message: t('orderForm.validation.amountMin') }
              });

              return (
                <div
                  className="relative transition-all overflow-hidden"
                  style={{
                    backgroundColor: personalization.input.background || '#FFFFFF',
                    borderStyle: 'solid',
                    borderWidth: isAmountFocused ? '2px' : '1px',
                    borderColor: isAmountFocused
                      ? (personalization.input.focusBorder || personalization.button.primary || '#9333EA')
                      : (personalization.input.border || '#D1D5DB'),
                    borderRadius: personalization.input.borderRadius && !personalization.input.borderRadius.includes('#')
                      ? (personalization.input.borderRadius === 'rounded-none' ? '0' :
                        personalization.input.borderRadius === 'rounded-sm' ? '0.125rem' :
                          personalization.input.borderRadius === 'rounded' ? '0.25rem' :
                            personalization.input.borderRadius === 'rounded-md' ? '0.375rem' :
                              personalization.input.borderRadius === 'rounded-lg' ? '0.5rem' :
                                personalization.input.borderRadius === 'rounded-xl' ? '0.75rem' :
                                  personalization.input.borderRadius === 'rounded-2xl' ? '1rem' :
                                    personalization.input.borderRadius === 'rounded-3xl' ? '1.5rem' :
                                      personalization.input.borderRadius === 'rounded-full' ? '9999px' : '1rem')
                      : '1rem',
                    boxShadow: 'none'
                  }}
                >
                  <input
                    id="amount"
                    type="number"
                    step="0.01"
                    className="w-full text-center text-6xl font-light bg-transparent border-none outline-none transition-colors"
                    style={{
                      color: form.watch("amount") && parseFloat(form.watch("amount")) > 0
                        ? personalization.text.primary
                        : personalization.text.secondary,
                    }}
                    placeholder="0,00"
                    onWheel={(e) => e.currentTarget.blur()}
                    {...amountRegister}
                    onFocus={() => setIsAmountFocused(true)}
                    onBlur={(e) => {
                      amountBlur(e);
                      setIsAmountFocused(false);
                    }}
                  />
                  <span
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-6xl font-light"
                    style={{ color: personalization.text.primary }}
                  >
                    €
                  </span>
                </div>
              );
            })()}
            {form.formState.errors.amount && (form.formState.touchedFields.amount || form.formState.isSubmitted) && (
              <p
                className="mt-4 text-sm text-center"
                style={{ color: '#DC2626' }}
              >
                {form.formState.errors.amount.message}
              </p>
            )}
          </div>
        </div>

        {/* Order Details Section */}
        <div
          className={`p-8 ${getCardStyles().className}`}
          style={{
            backgroundColor: personalization.card.background,
            borderColor: personalization.card.border,
            ...getCardStyles().style
          }}
        >
          <h3
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: personalization.text.heading }}
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
              style={{ color: personalization.icon.primary }}
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
            </svg>
            {t('orderForm.sections.orderDetails')}
          </h3>
          <div className="space-y-4">
            <div>
              {(() => {
                const inputStyles = getInputStyles();
                const focusStyles = getInputFocusStyles();
                return (
                  <input
                    id="productReference"
                    type="text"
                    className={`w-full px-6 py-4 border ${inputStyles.className} focus:border-transparent`}
                    style={{
                      ...inputStyles.style,
                      ...focusStyles
                    }}
                    placeholder={t('orderForm.validation.referencePlaceholder')}
                    {...form.register("productReference", { required: t('orderForm.validation.referenceRequired') })}
                  />
                );
              })()}
              {form.formState.errors.productReference && (form.formState.touchedFields.productReference || form.formState.isSubmitted) && (
                <p
                  className="mt-2 text-sm"
                  style={{ color: '#DC2626' }}
                >
                  {form.formState.errors.productReference.message}
                </p>
              )}
            </div>

            <div>
              {(() => {
                const inputStyles = getInputStyles();
                const focusStyles = getInputFocusStyles();
                return (
                  <input
                    id="email"
                    type="email"
                    className={`w-full px-6 py-4 border ${inputStyles.className} focus:border-transparent`}
                    style={{
                      ...inputStyles.style,
                      ...focusStyles
                    }}
                    placeholder={t('orderForm.steps.orderDetails.email.placeholder')}
                    readOnly={!!currentUser}
                    {...form.register("email", {
                      required: t('orderForm.validation.emailRequired'),
                      pattern: {
                        value: EMAIL_REGEX,
                        message: t('orderForm.validation.emailInvalid')
                      }
                    })}
                  />
                );
              })()}
              {form.formState.errors.email && (form.formState.touchedFields.email || form.formState.isSubmitted) && (
                <p
                  className="mt-2 text-sm"
                  style={{ color: '#DC2626' }}
                >
                  {form.formState.errors.email.message}
                </p>
              )}
              <p className="mt-2 text-[11px] opacity-70 italic" style={{ color: personalization.text.secondary }}>
                {t('orderForm.tips.mainInfo')}
              </p>
            </div>

            {/* Afficher la section Articles uniquement s'il y a des champs configurés */}
            {(() => {
              const modelFormat = configuration?.personalization?.productModelConfig?.format || configuration?.productModelConfig?.format || 'structured';
              const isCustomFormat = modelFormat === 'custom';
              const customFields = configuration?.personalization?.productModelConfig?.customFields || configuration?.productModelConfig?.customFields || [];

              // Ne pas afficher si format custom et aucun champ configuré
              if (isCustomFormat && customFields.length === 0) {
                return null;
              }

              return (
                <div className="space-y-4">
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: personalization.text.primary }}
                  >
                    {t('orderForm.product.itemsTitle')}
                  </label>
                  {/* Détecter le format depuis la configuration (par défaut: structured pour rétrocompatibilité) */}
                  {(() => {
                    const isFreeFormat = modelFormat === 'free';
                    const modelPlaceholder = configuration?.personalization?.productModelConfig?.modelPlaceholder || configuration?.productModelConfig?.modelPlaceholder || t('orderForm.product.model.placeholder');
                    const typePlaceholder = configuration?.personalization?.productModelConfig?.typePlaceholder || configuration?.productModelConfig?.typePlaceholder || t('orderForm.product.model.placeholder');
                    const sizePlaceholder = configuration?.personalization?.productModelConfig?.sizePlaceholder || configuration?.productModelConfig?.sizePlaceholder || t('orderForm.product.size.placeholder');

                    if (isFreeFormat) {
                      // Format libre : un seul champ texte
                      return (
                        <>
                          {fields.map((field, index) => (
                            <div key={field.id} className="flex gap-3 items-start">
                              <div className="flex-1">
                                {(() => {
                                  const inputStyles = getInputStyles();
                                  const focusStyles = getInputFocusStyles();
                                  return (
                                    <input
                                      type="text"
                                      className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                      style={{
                                        ...inputStyles.style,
                                        ...focusStyles
                                      }}
                                      placeholder={modelPlaceholder}
                                      {...form.register(`productItems.${index}.modelName` as const, { required: t('orderForm.validation.required') })}
                                    />
                                  );
                                })()}
                                {form.formState.errors.productItems?.[index]?.modelName && (
                                  <p
                                    className="mt-1 text-xs"
                                    style={{ color: '#DC2626' }}
                                  >
                                    {form.formState.errors.productItems[index]?.modelName?.message}
                                  </p>
                                )}
                                {/* Champ caché pour size (non requis en format libre) */}
                                <input
                                  type="hidden"
                                  {...form.register(`productItems.${index}.size` as const)}
                                />
                              </div>

                              {fields.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="p-3 rounded-xl transition-colors mt-[1px]"
                                  style={{
                                    color: '#DC2626',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#FEF2F2';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                  title={t('common.delete')}
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={() => append({ modelName: '', size: '' })}
                            className="flex items-center gap-2 text-sm font-medium px-2 py-1 rounded-lg transition-colors"
                            style={{
                              color: personalization.button.primary || '#2563EB',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = personalization.button.primaryHover || '#1D4ED8';
                              e.currentTarget.style.backgroundColor = getColorWithOpacity(personalization.button.primary, 0.1);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = getPrimaryColor(personalization.button.primary);
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Plus size={16} />
                            {t('orderForm.product.addItem')}
                          </button>
                        </>
                      );
                    } else if (isCustomFormat && customFields.length > 0) {
                      // Format personnalisé : afficher tous les champs personnalisés
                      return (
                        <>
                          {fields.map((field, index) => (
                            <div key={field.id} className="flex gap-3 items-start">
                              <div className={`grid gap-3 flex-1 ${customFields.length === 1 ? 'grid-cols-1' : customFields.length === 2 ? 'grid-cols-2' : customFields.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                {customFields.map((customField: any, fieldIndex: number) => {
                                  const inputStyles = getInputStyles();
                                  const focusStyles = getInputFocusStyles();
                                  const fieldKey = customField.key || `field_${fieldIndex}`;
                                  const registerKey = `productItems.${index}.customFields.${fieldKey}` as const;
                                  const isRequired = customField.required || false;

                                  return (
                                    <div key={fieldKey}>
                                      <label
                                        className="block text-xs font-medium mb-1"
                                        style={{ color: personalization.text.secondary || '#6B7280' }}
                                      >
                                        {customField.label || fieldKey}
                                        {isRequired && <span style={{ color: '#DC2626' }}> *</span>}
                                      </label>
                                      {customField.type === 'textarea' ? (
                                        <textarea
                                          className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent resize-none`}
                                          style={{
                                            ...inputStyles.style,
                                            ...focusStyles
                                          }}
                                          placeholder={customField.placeholder || ''}
                                          rows={3}
                                          {...form.register(registerKey, {
                                            required: isRequired ? t('orderForm.validation.required') : false
                                          })}
                                        />
                                      ) : (
                                        <input
                                          type={customField.type || 'text'}
                                          className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                          style={{
                                            ...inputStyles.style,
                                            ...focusStyles
                                          }}
                                          placeholder={customField.placeholder || ''}
                                          {...form.register(registerKey, {
                                            required: isRequired ? t('orderForm.validation.required') : false
                                          })}
                                        />
                                      )}
                                      {form.formState.errors.productItems?.[index]?.customFields?.[fieldKey] && (
                                        <p
                                          className="mt-1 text-xs"
                                          style={{ color: '#DC2626' }}
                                        >
                                          {form.formState.errors.productItems[index]?.customFields?.[fieldKey]?.message}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {fields.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="p-3 rounded-xl transition-colors mt-[1px]"
                                  style={{
                                    color: '#DC2626',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#FEF2F2';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                  title={t('common.delete')}
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={() => {
                              const newItem: any = { modelName: '', size: '' };
                              customFields.forEach((customField: any) => {
                                const fieldKey = customField.key || `field_${customFields.indexOf(customField)}`;
                                newItem.customFields = newItem.customFields || {};
                                newItem.customFields[fieldKey] = '';
                              });
                              append(newItem);
                            }}
                            className="flex items-center gap-2 text-sm font-medium px-2 py-1 rounded-lg transition-colors"
                            style={{
                              color: personalization.button.primary || '#2563EB',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = personalization.button.primaryHover || '#1D4ED8';
                              e.currentTarget.style.backgroundColor = getColorWithOpacity(personalization.button.primary, 0.1);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = getPrimaryColor(personalization.button.primary);
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Plus size={16} />
                            {t('orderForm.product.addItem')}
                          </button>
                        </>
                      );
                    } else {
                      // Format structuré : deux champs (Type + Taille)
                      return (
                        <>
                          {fields.map((field, index) => (
                            <div key={field.id} className="flex gap-3 items-start">
                              <div className="grid grid-cols-2 gap-3 flex-1">
                                <div>
                                  {(() => {
                                    const inputStyles = getInputStyles();
                                    const focusStyles = getInputFocusStyles();
                                    return (
                                      <>
                                        <input
                                          type="text"
                                          className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                          style={{
                                            ...inputStyles.style,
                                            ...focusStyles
                                          }}
                                          placeholder={typePlaceholder}
                                          {...form.register(`productItems.${index}.modelName` as const, { required: t('orderForm.validation.required') })}
                                        />
                                        {form.formState.errors.productItems?.[index]?.modelName && (
                                          <p
                                            className="mt-1 text-xs"
                                            style={{ color: '#DC2626' }}
                                          >
                                            {form.formState.errors.productItems[index]?.modelName?.message}
                                          </p>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                <div>
                                  {(() => {
                                    const inputStyles = getInputStyles();
                                    const focusStyles = getInputFocusStyles();
                                    return (
                                      <input
                                        type="text"
                                        className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                        style={{
                                          ...inputStyles.style,
                                          ...focusStyles
                                        }}
                                        placeholder={sizePlaceholder}
                                        {...form.register(`productItems.${index}.size` as const, { required: t('orderForm.validation.required') })}
                                      />
                                    );
                                  })()}
                                  {form.formState.errors.productItems?.[index]?.size && (
                                    <p
                                      className="mt-1 text-xs"
                                      style={{ color: '#DC2626' }}
                                    >
                                      {form.formState.errors.productItems[index]?.size?.message}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {fields.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="p-3 rounded-xl transition-colors mt-[1px]"
                                  style={{
                                    color: '#DC2626',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#FEF2F2';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                  title={t('common.delete')}
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={() => append({ modelName: '', size: '' })}
                            className="flex items-center gap-2 text-sm font-medium px-2 py-1 rounded-lg transition-colors"
                            style={{
                              color: personalization.button.primary || '#2563EB',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = personalization.button.primaryHover || '#1D4ED8';
                              e.currentTarget.style.backgroundColor = getColorWithOpacity(personalization.button.primary, 0.1);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = getPrimaryColor(personalization.button.primary);
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Plus size={16} />
                            {t('orderForm.product.addItem')}
                          </button>
                        </>
                      );
                    }
                  })()}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Existing Order Information Display - Only for merged orders */}
        {isMergedOrder && (
          <div
            className={`rounded-xl p-4`}
            style={{
              backgroundColor: personalization.card.background,
              borderColor: personalization.card.border || '#F3F4F6',
              borderWidth: '1px',
              borderStyle: 'solid',
              ...(personalization.card.borderRadius && !personalization.card.borderRadius.includes('#') ? {} : { borderRadius: '0.75rem' }),
            }}
          >
            {/* Header minimaliste */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center mr-2"
                  style={{
                    backgroundColor: personalization.button.primary
                      ? `${personalization.button.primary}20`
                      : personalization.badge.background
                  }}
                >
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    style={{ color: personalization.icon.primary }}
                  >
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3
                  className="text-base font-medium"
                  style={{ color: personalization.text.heading }}
                >
                  {t('orderForm.mergedOrder.title')}
                </h3>
              </div>
              <div
                className="px-2 py-1 rounded-md"
                style={{
                  backgroundColor: personalization.button.primary
                    ? `${personalization.button.primary}20`
                    : personalization.badge.background
                }}
              >
                <span
                  className="text-xs font-medium"
                  style={{ color: personalization.icon.primary }}
                >
                  {t('orderForm.mergedOrder.freeShipping')}
                </span>
              </div>
            </div>

            {/* Client et livraison : bloc compact */}
            <div className="max-w-sm mx-auto text-center">
              <div
                className="rounded-xl px-4 py-2.5 space-y-2"
                style={{
                  background: personalization.button?.primary
                    ? `linear-gradient(135deg, ${personalization.button.primary}08 0%, transparent 100%)`
                    : 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, transparent 100%)',
                }}
              >
                <div>
                  <p
                    className="text-[10px] uppercase tracking-widest mb-0 opacity-80"
                    style={{ color: personalization.text.secondary }}
                  >
                    {t('orderForm.mergedOrder.client')}
                  </p>
                  <p
                    className="text-sm font-medium"
                    style={{ color: personalization.text.heading }}
                  >
                    {orderData.firstName && orderData.lastName
                      ? `${orderData.firstName} ${orderData.lastName}`
                      : orderData.firstName || orderData.lastName || t('orderForm.mergedOrder.notProvided')
                    }
                  </p>
                </div>
                <div
                  className="h-px opacity-30"
                  style={{ backgroundColor: personalization.card?.border || '#E5E7EB' }}
                />
                <div className="text-left">
                  <p
                    className="text-[10px] uppercase tracking-widest mb-0 opacity-80"
                    style={{ color: personalization.text.secondary }}
                  >
                    {t('orderForm.mergedOrder.delivery')}
                  </p>
                  <p
                    className="text-xs leading-snug"
                    style={{ color: personalization.text.primary }}
                  >
                    {selectedPickupPoint
                      ? `${selectedPickupPoint.name}, ${selectedPickupPoint.address}${selectedPickupPoint.postalCode ? `, ${selectedPickupPoint.postalCode}` : ''
                      } ${selectedPickupPoint.city || ''}${selectedPickupPoint.country ? `, ${selectedPickupPoint.country}` : ''
                      }`
                      : `${orderData.address || t('orderForm.mergedOrder.notProvidedAddress')}, ${orderData.postalCode || ''
                      } ${orderData.city || ''}, ${orderData.country || ''}`}
                  </p>
                </div>
              </div>
              <div className="mt-2 space-y-0.5">
                {getMergeConfigInfo.hasConfig && (
                  <p className="text-[10px] opacity-90" style={{ color: personalization.text.secondary }}>
                    {getMergeConfigInfo.text}
                  </p>
                )}
                <p className="text-[10px] opacity-90" style={{ color: personalization.text.secondary }}>
                  {t('orderForm.mergedOrder.deliveryConfirmation')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Personal Information Section - Hidden for merged orders and when email is invalid */}
        {!isMergedOrder && watchedEmail && isValidEmail(watchedEmail) && (
          <div
            className={`p-6 ${getCardStyles().className}`}
            style={{
              backgroundColor: personalization.card.background,
              borderColor: personalization.card.border,
              ...getCardStyles().style
            }}
          >
            <h3
              className="text-lg font-semibold mb-4 flex items-center"
              style={{ color: personalization.text.heading }}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
                style={{ color: personalization.icon.primary }}
              >
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              {t('orderForm.sections.personalInfo')}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium mb-2"
                    style={{ color: personalization.text.primary }}
                  >
                    {t('orderForm.steps.shippingInfo.lastName.label')}
                  </label>
                  {(() => {
                    const inputStyles = getInputStyles();
                    const focusStyles = getInputFocusStyles();
                    return (
                      <input
                        id="lastName"
                        type="text"
                        className={`w-full px-4 py-3 border ${inputStyles.className} focus:border-transparent`}
                        style={{
                          ...inputStyles.style,
                          ...focusStyles
                        }}
                        placeholder={t('orderForm.steps.shippingInfo.lastName.placeholder')}
                        {...form.register("lastName", { required: t('orderForm.validation.lastNameRequired') })}
                      />
                    );
                  })()}
                  {form.formState.errors.lastName && (form.formState.touchedFields.lastName || form.formState.isSubmitted) && (
                    <p
                      className="mt-1 text-sm"
                      style={{ color: '#DC2626' }}
                    >
                      {form.formState.errors.lastName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium mb-2"
                    style={{ color: personalization.text.primary }}
                  >
                    {t('orderForm.steps.shippingInfo.firstName.label')}
                  </label>
                  {(() => {
                    const inputStyles = getInputStyles();
                    const focusStyles = getInputFocusStyles();
                    return (
                      <input
                        id="firstName"
                        type="text"
                        className={`w-full px-4 py-3 border ${inputStyles.className} focus:border-transparent`}
                        style={{
                          ...inputStyles.style,
                          ...focusStyles
                        }}
                        placeholder={t('orderForm.steps.shippingInfo.firstName.placeholder')}
                        {...form.register("firstName", { required: t('orderForm.validation.firstNameRequired') })}
                      />
                    );
                  })()}
                  {form.formState.errors.firstName && (form.formState.touchedFields.firstName || form.formState.isSubmitted) && (
                    <p
                      className="mt-1 text-sm"
                      style={{ color: '#DC2626' }}
                    >
                      {form.formState.errors.firstName.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="tiktokUsername"
                  className="block text-sm font-medium mb-2"
                  style={{ color: personalization.text.primary }}
                >
                  {t('orderForm.steps.orderDetails.tiktok.label')}
                </label>
                {(() => {
                  const inputStyles = getInputStyles();
                  const focusStyles = getInputFocusStyles();
                  return (
                    <input
                      id="tiktokUsername"
                      type="text"
                      className={`w-full px-4 py-3 border ${inputStyles.className} focus:border-transparent`}
                      style={{
                        ...inputStyles.style,
                        ...focusStyles
                      }}
                      placeholder={t('orderForm.validation.tiktokPlaceholder')}
                      {...form.register("tiktokUsername", { required: t('orderForm.validation.tiktokRequired') })}
                    />
                  );
                })()}
                {form.formState.errors.tiktokUsername && (form.formState.touchedFields.tiktokUsername || form.formState.isSubmitted) && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: '#DC2626' }}
                  >
                    {form.formState.errors.tiktokUsername.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium mb-2"
                  style={{ color: personalization.text.primary }}
                >
                  {t('orderForm.steps.shippingInfo.phone.label')}
                </label>
                <div id="phone-input-container">
                  <PhoneInput
                    value={form.watch("phone") || ""}
                    onChange={(value) => form.setValue("phone", value)}
                    country={form.watch("country") || "FR"}
                    error={form.formState.errors.phone?.message}
                    placeholder={t('orderForm.steps.shippingInfo.phone.placeholder')}
                    required={true}
                    personalization={{
                      input: {
                        background: personalization.input.background,
                        border: personalization.input.border,
                        focusBorder: personalization.input.focusBorder,
                        focusRing: personalization.input.focusRing,
                        text: personalization.input.text,
                        placeholder: personalization.input.placeholder,
                        borderRadius: personalization.input.borderRadius,
                      },
                      text: {
                        primary: personalization.text.primary,
                        secondary: personalization.text.secondary,
                      },
                    }}
                    onValidationChange={(isValid) => {
                      if (!isValid && form.watch("phone")) {
                        form.setError("phone", {
                          type: "manual",
                          message: t('orderForm.validation.phoneInvalid')
                        });
                      } else {
                        form.clearErrors("phone");
                      }
                    }}
                  />
                </div>
                <input
                  type="hidden"
                  {...form.register("phone", {
                    required: t('orderForm.validation.phoneRequired')
                  })}
                />
              </div>

              {/* Checkbox pour se souvenir des informations - uniquement si connecté */}
              {currentUser && (
                <div className="mt-4 flex items-start">
                  <input
                    id="rememberMyInfo"
                    type="checkbox"
                    checked={rememberMyInfo}
                    onChange={(e) => setRememberMyInfo(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded"
                    style={{
                      color: personalization.button.primary || '#2563EB',
                      borderColor: personalization.input.border || '#D1D5DB',
                      '--tw-ring-color': personalization.button.primary || '#2563EB',
                    } as any}
                  />
                  <label
                    htmlFor="rememberMyInfo"
                    className="ml-3 text-sm"
                    style={{ color: personalization.text.primary }}
                  >
                    {t('orderForm.rememberMyInfo') || 'Se souvenir de mes informations pour mes prochaines commandes'}
                  </label>
                </div>
              )}

              {/* Adresse du domicile avec Google Autocomplete */}
              <div
                className="mt-4 pt-4 border-t"
                style={{ borderColor: personalization.card.border || '#E5E7EB' }}
              >
                <div className="mb-3">
                  <h4
                    className="text-sm font-semibold"
                    style={{ color: personalization.icon.primary }}
                  >
                    {t('orderForm.steps.shippingInfo.address.homeAddressLabel')}
                    {(() => {
                      const selectedMethod = getShippingMethodById(form.watch('deliveryMethod') || '');
                      const isPickupMethod = selectedMethod && selectedMethod.isHomeDelivery === false;
                      return isPickupMethod ? ` ${t('orderForm.steps.shippingInfo.address.requiredForPickup')}` : '';
                    })()}
                  </h4>
                </div>

                <div className="space-y-4">
                  {isGoogleAvailable && (
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: personalization.text.primary }}
                      >
                        {t('orderForm.steps.shippingInfo.address.searchLabel')}
                      </label>
                      <MemoizedGooglePlacesSearch
                        isLoaded={isLoaded}
                        loadError={loadError}
                        autocompleteText={t('orderForm.google.autocompleteText')}
                        placeholder={t('orderForm.steps.shippingInfo.address.searchPlaceholder')}
                        configuration={configuration}
                        availableShippingMethods={availableShippingMethods}
                        selectedFormCountry={watchedCountry}
                        initialValue={(() => {
                          const fa = form.getValues('fullAddress');
                          if (fa && String(fa).trim()) return String(fa);
                          const tokens = [
                            form.getValues('houseNumber'),
                            form.getValues('address'),
                            form.getValues('city'),
                            form.getValues('postalCode')
                          ].filter(v => String(v || '').trim());
                          return tokens.length >= 2 ? tokens.join(' ') : '';
                        })()}
                        onGoogleMapsLoadSuccess={handleGoogleMapsLoadSuccess}
                        onGoogleMapsLoadFailure={handleGoogleMapsLoadFailure}
                        onAddressSelect={handleAddressSelect}
                      />
                      {!showManualAddress && (
                        <>
                          <input
                            type="hidden"
                            {...form.register("houseNumber", {
                              required: t('orderForm.steps.shippingInfo.address.houseNumberRequired') || "Numéro requis",
                              maxLength: {
                                value: 8,
                                message: t('orderForm.steps.shippingInfo.address.houseNumberMaxLength') || "Le numéro de rue doit contenir uniquement le numéro et ne pas dépasser 8 caractères"
                              }
                            })}
                          />
                          <input
                            type="hidden"
                            {...form.register("address", {
                              required: t('orderForm.steps.shippingInfo.address.error'),
                              validate: {
                                notEmpty: (value) => {
                                  if (!value || String(value).trim() === '') {
                                    return t('orderForm.steps.shippingInfo.address.error');
                                  }
                                  return true;
                                }
                              }
                            })}
                          />
                          <input
                            type="hidden"
                            {...form.register("postalCode", {
                              required: t('orderForm.steps.shippingInfo.postalCode.error'),
                              pattern: {
                                value: /^[A-Z0-9\s-]{3,10}$/i,
                                message: t('orderForm.steps.shippingInfo.postalCode.invalidFormat')
                              }
                            })}
                          />
                          <input
                            type="hidden"
                            {...form.register("city", {
                              required: t('orderForm.steps.shippingInfo.city.error')
                            })}
                          />
                          <input
                            type="hidden"
                            {...form.register("country", {
                              required: t('orderForm.validation.countryRequired')
                            })}
                          />
                        </>
                      )}
                      {/* Affichage des messages d'erreur sous l'autocomplete */}
                      {form.formState.errors.houseNumber && (form.formState.errors.houseNumber.type === 'manual' || form.formState.touchedFields.houseNumber || form.formState.isSubmitted) && (
                        <p
                          className="mt-2 text-sm"
                          style={{ color: '#DC2626' }}
                        >
                          {form.formState.errors.houseNumber.message as string}
                        </p>
                      )}
                      {form.formState.errors.address && (form.formState.touchedFields.address || form.formState.isSubmitted) && (
                        <p
                          className="mt-1 text-sm"
                          style={{ color: '#DC2626' }}
                        >
                          {form.formState.errors.address.message as string}
                        </p>
                      )}
                      {form.formState.errors.postalCode && (form.formState.touchedFields.postalCode || form.formState.isSubmitted) && (
                        <p
                          className="mt-1 text-sm"
                          style={{ color: '#DC2626' }}
                        >
                          {form.formState.errors.postalCode.message as string}
                        </p>
                      )}
                      {form.formState.errors.city && (form.formState.touchedFields.city || form.formState.isSubmitted) && (
                        <p
                          className="mt-1 text-sm"
                          style={{ color: '#DC2626' }}
                        >
                          {form.formState.errors.city.message as string}
                        </p>
                      )}
                      {form.formState.errors.country && (form.formState.touchedFields.country || form.formState.isSubmitted) && (
                        <p
                          className="mt-1 text-sm"
                          style={{ color: '#DC2626' }}
                        >
                          {form.formState.errors.country.message as string}
                        </p>
                      )}

                      <div className="mt-3">
                        <button
                          type="button"
                          className="text-sm hover:underline transition-colors"
                          style={{
                            color: personalization.button.primary || '#2563EB',
                          }}
                          onClick={() => {
                            const newManualMode = !showManualAddress;
                            setShowManualAddress(newManualMode);

                            // Déclencher une revalidation après le changement de mode
                            // pour s'assurer que les champs sont correctement validés
                            setTimeout(() => {
                              // Effacer les erreurs existantes sur les champs d'adresse
                              form.clearErrors(['address', 'houseNumber', 'city', 'postalCode', 'country']);

                              // Revalider les champs si ils ont des valeurs
                              const fieldsToValidate = ['address', 'houseNumber', 'city', 'postalCode', 'country'];
                              const fieldsWithValues = fieldsToValidate.filter(field => {
                                const value = form.watch(field as any);
                                return value && String(value).trim() !== '';
                              });

                              if (fieldsWithValues.length > 0) {
                                form.trigger(fieldsWithValues as any);
                              }
                            }, 100);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = personalization.button.primaryHover || '#1D4ED8';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = personalization.button.primary || '#2563EB';
                          }}
                        >
                          {showManualAddress
                            ? (t('orderForm.steps.shippingInfo.address.hideManual') || 'Masquer la saisie manuelle')
                            : (t('orderForm.steps.shippingInfo.address.enterManually') || 'Vous ne trouvez pas votre adresse ? Saisissez-la manuellement')}
                        </button>
                      </div>

                      {showManualAddress && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label
                              className="block text-sm font-medium mb-1"
                              style={{ color: personalization.text.primary }}
                            >
                              {t('orderForm.steps.shippingInfo.address.houseNumberLabel') || 'Numéro'}
                            </label>
                            {(() => {
                              const inputStyles = getInputStyles();
                              const focusStyles = getInputFocusStyles();
                              return (
                                <input
                                  type="text"
                                  className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                  style={{
                                    ...inputStyles.style,
                                    ...focusStyles
                                  }}
                                  {...form.register('houseNumber', {
                                    required: t('orderForm.steps.shippingInfo.address.houseNumberRequired') || 'Numéro requis',
                                    maxLength: {
                                      value: 8,
                                      message: t('orderForm.steps.shippingInfo.address.houseNumberMaxLength') || 'Le numéro de rue doit contenir uniquement le numéro et ne pas dépasser 8 caractères'
                                    }
                                  })}
                                />
                              );
                            })()}
                          </div>
                          <div>
                            <label
                              className="block text-sm font-medium mb-1"
                              style={{ color: personalization.text.primary }}
                            >
                              {t('orderForm.steps.shippingInfo.address.streetLabel') || 'Rue'}
                            </label>
                            {(() => {
                              const inputStyles = getInputStyles();
                              const focusStyles = getInputFocusStyles();
                              return (
                                <input
                                  type="text"
                                  className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                  style={{
                                    ...inputStyles.style,
                                    ...focusStyles
                                  }}
                                  {...form.register('address', {
                                    required: t('orderForm.steps.shippingInfo.address.error'),
                                    validate: {
                                      notEmpty: (value) => {
                                        if (!value || String(value).trim() === '') {
                                          return t('orderForm.steps.shippingInfo.address.error');
                                        }
                                        return true;
                                      }
                                    }
                                  })}
                                />
                              );
                            })()}
                          </div>
                          <div>
                            <label
                              className="block text-sm font-medium mb-1"
                              style={{ color: personalization.text.primary }}
                            >
                              {t('orderForm.steps.shippingInfo.city.label') || 'Ville'}
                            </label>
                            {(() => {
                              const inputStyles = getInputStyles();
                              const focusStyles = getInputFocusStyles();
                              return (
                                <input
                                  type="text"
                                  className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                  style={{
                                    ...inputStyles.style,
                                    ...focusStyles
                                  }}
                                  {...form.register('city', {
                                    required: t('orderForm.steps.shippingInfo.city.error')
                                  })}
                                />
                              );
                            })()}
                          </div>
                          <div>
                            <label
                              className="block text-sm font-medium mb-1"
                              style={{ color: personalization.text.primary }}
                            >
                              {t('orderForm.steps.shippingInfo.postalCode.label') || 'Code postal'}
                            </label>
                            {(() => {
                              const inputStyles = getInputStyles();
                              const focusStyles = getInputFocusStyles();
                              return (
                                <input
                                  type="text"
                                  className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                  style={{
                                    ...inputStyles.style,
                                    ...focusStyles
                                  }}
                                  {...form.register('postalCode', {
                                    required: t('orderForm.steps.shippingInfo.postalCode.error'),
                                    pattern: {
                                      value: /^[A-Z0-9\s-]{3,10}$/i,
                                      message: t('orderForm.steps.shippingInfo.postalCode.invalidFormat')
                                    }
                                  })}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setPickupSearchPostalCode(v);
                                    if (postalCodeInputRef.current) {
                                      postalCodeInputRef.current.value = v;
                                    }
                                  }}
                                />
                              );
                            })()}
                          </div>
                          <div className="md:col-span-2">
                            <label
                              className="block text-sm font-medium mb-1"
                              style={{ color: personalization.text.primary }}
                            >
                              {t('orderForm.steps.shippingInfo.country.label') || 'Pays'}
                            </label>
                            {(() => {
                              const inputStyles = getInputStyles();
                              const focusStyles = getInputFocusStyles();
                              return (
                                <select
                                  className={`w-full px-4 py-3 border text-sm ${inputStyles.className} focus:border-transparent`}
                                  style={{
                                    ...inputStyles.style,
                                    ...focusStyles
                                  }}
                                  {...form.register('country', {
                                    required: t('orderForm.validation.countryRequired') || 'Pays requis',
                                    onChange: (e) => {
                                      const code = (e.target.value || '').toUpperCase();
                                      const label = regionNames.of(code) || code;
                                      setCountryDisplayName(label as string);
                                      // Désélectionner la méthode de livraison au changement de pays
                                      form.setValue('deliveryMethod', null, { shouldValidate: true });
                                      // Réinitialiser le point relais s'il y en a un
                                      setSelectedPickupPoint(null);
                                      setContextSelectedPickupPoint(null);
                                      form.setValue('pickupPoint', undefined, { shouldValidate: true });
                                      setPickupPoints([]);
                                      setHasSearchedPickupPoints(false);
                                      setPickupSearchPostalCode('');
                                    }
                                  })}
                                >
                                  <option value="">{t('orderForm.steps.shippingInfo.country.placeholder') || 'Sélectionner un pays'}</option>
                                  {availableCountries.map((c) => (
                                    <option key={c} value={c}>{(regionNames.of(c) as string) || c}</option>
                                  ))}
                                </select>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {isGoogleAvailable && [form.watch("houseNumber"), form.watch("address"), form.watch("city"), form.watch("country"), form.watch("postalCode")].every(v => String(v || '').trim()) ? (
                    <div className="mt-4">
                      <div
                        className={`flex items-start gap-3 p-3 ${personalization.card.borderRadius && !personalization.card.borderRadius.includes('#') ? personalization.card.borderRadius : 'rounded-xl'} ${personalization.card.shadow && !personalization.card.shadow.includes('#') ? personalization.card.shadow : 'shadow-sm'}`}
                        style={{
                          backgroundColor: personalization.card.background,
                          borderColor: personalization.card.border || '#E5E7EB',
                          borderWidth: personalization.card.border ? '1px' : '0',
                          borderStyle: personalization.card.border ? 'solid' : 'none',
                        }}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: getColorWithOpacity(personalization.button.primary, 0.2) }}
                        >
                          <MapPin
                            className="w-4 h-4"
                            style={{ color: personalization.icon.primary }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          {(
                            form.watch("houseNumber") || form.watch("address")
                          ) && (
                              <p
                                className="text-sm font-medium truncate"
                                style={{ color: personalization.text.primary }}
                              >
                                {[form.watch("houseNumber"), form.watch("address")]
                                  .filter(Boolean)
                                  .join(' ')}
                              </p>
                            )}
                          {(
                            form.watch("city") || form.watch("country") || form.watch("postalCode")
                          ) && (
                              <p
                                className="text-xs mt-1 truncate"
                                style={{ color: personalization.text.secondary }}
                              >
                                {[form.watch("city"), form.watch("country"), form.watch("postalCode")]
                                  .filter(Boolean)
                                  .join(' ')}
                              </p>
                            )}
                        </div>
                        {(() => {
                          const isAddressComplete = [
                            form.watch("houseNumber"),
                            form.watch("address"),
                            form.watch("city"),
                            form.watch("country"),
                            form.watch("postalCode")
                          ].every((v) => String(v || '').trim());
                          const isAutocompleteVisible = isGoogleAvailable && (isEditingAddress || !isAddressComplete);
                          if (isAutocompleteVisible) return null;
                          return null;
                        })()}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delivery Method Section */}
        {!isMergedOrder &&
          !isEditingAddress &&
          form.watch("email") && form.watch("email").trim() !== "" &&
          form.watch("houseNumber") && form.watch("houseNumber").trim() !== "" &&
          form.watch("address") && form.watch("address").trim() !== "" &&
          form.watch("city") && form.watch("city").trim() !== "" &&
          form.watch("postalCode") && form.watch("postalCode").trim() !== "" &&
          form.watch("country") && String(form.watch("country")).trim() !== "" && (
            <div
              className={`p-6 ${getCardStyles().className}`}
              style={{
                backgroundColor: personalization.card.background,
                borderColor: personalization.card.border,
                ...getCardStyles().style
              }}
            >
              <div className="mb-4">
                <h3
                  className="text-lg font-semibold flex items-center"
                  style={{ color: personalization.text.heading }}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    style={{ color: personalization.icon.primary }}
                  >
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1V8a1 1 0 00-1-1h-3z" />
                  </svg>
                  {t('orderForm.sections.deliveryMethod')}
                </h3>
                {/* Message mis en avant : frais de livraison payés une seule fois (période ou même jour) */}
                {getMergeConfigInfo.hasConfig && (
                  <div
                    className="mt-2 mb-2 px-4 py-2.5 rounded-xl border flex items-center gap-2 min-w-0"
                    style={{
                      backgroundColor: personalization.card?.background ? `${personalization.card.background}99` : 'rgba(139, 92, 246, 0.08)',
                      borderColor: personalization.card?.border || 'rgba(139, 92, 246, 0.25)',
                    }}
                  >
                    <Info
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: personalization.icon?.primary || '#8B5CF6' }}
                      aria-hidden
                    />
                    <p
                      className="text-sm font-medium"
                      style={{ color: personalization.text?.heading || personalization.text?.primary || '#374151' }}
                    >
                      {getMergeConfigInfo.text}
                    </p>
                  </div>
                )}
              </div>

              {/* Sélection du mode de livraison */}
              <div id="shipping-methods-container">
                <ShippingMethodSelector
                  selectedMethod={watchedDeliveryMethod}
                  onMethodChange={handleMethodChange}
                  selectedCountry={watchedCountry || undefined}
                />

                {/* Message d'erreur pour mode de livraison manquant */}
                {(() => {
                  const selectedCountry = form.watch("country");
                  const hasDeliveryMethod = !!form.watch('deliveryMethod');
                  const methodsForCountry = selectedCountry ? getShippingMethodsForCountry(selectedCountry) : [];

                  // Afficher une erreur si un pays est sélectionné mais pas de mode de livraison
                  if (selectedCountry && methodsForCountry.length > 0 && !hasDeliveryMethod) {
                    return (
                      <p
                        className="mt-2 text-sm"
                        style={{ color: '#DC2626' }}
                      >
                        {t('orderForm.steps.shippingInfo.deliveryMethod.error') || 'Veuillez sélectionner un mode de livraison'}
                      </p>
                    );
                  }

                  return form.formState.errors.deliveryMethod && (
                    <p
                      className="mt-2 text-sm"
                      style={{ color: '#DC2626' }}
                    >
                      {form.formState.errors.deliveryMethod.message}
                    </p>
                  );
                })()}
              </div>

              {/* Point Relais Search - Only for pickup delivery methods */}
              {(() => {
                const selectedCountry = form.watch("country");
                const selectedMethod = getShippingMethodById(form.watch('deliveryMethod') || '');

                if (!selectedMethod || !selectedCountry) {
                  return false;
                }

                // Domicile : `useShippingMethods` expose `isHomeDelivery` (ex. type `home`, MR DOM).
                // Ne pas utiliser `servicePointInput === 'none'` ici : ce champ vient du catalogue Sendcloud
                // et n’existe pas sur les méthodes issues de la config orga → tout passait en « point relais ».
                if (selectedMethod.isHomeDelivery === true) {
                  return false;
                }

                return true;
              })() && (
                  <div className="mt-4 p-4 rounded-xl">
                    <h4
                      className="text-sm font-medium mb-3"
                      style={{ color: personalization.icon.primary }}
                    >
                      {t('orderForm.pickupSearch.title')}
                    </h4>

                    {/* Postal Code Input with Location Icon */}
                    <div className="mb-3">
                      <div className="relative">
                        {(() => {
                          const inputStyles = getInputStyles();
                          const focusStyles = getInputFocusStyles();
                          return (
                            <input
                              ref={postalCodeInputRef}
                              type="text"
                              placeholder={t('orderForm.pickupSearch.postalCodePlaceholder')}
                              className={`w-full px-4 py-3 pr-12 border text-sm ${inputStyles.className} focus:border-transparent`}
                              style={{
                                ...inputStyles.style,
                                ...focusStyles
                              }}
                              value={pickupSearchPostalCode}
                              onChange={(e) => {
                                const value = e.target.value;
                                console.log('📝 Changement de recherche:', value);
                                setPickupSearchPostalCode(value);
                              }}
                            />
                          );
                        })()}
                        <button
                          type="button"
                          onClick={getUserLocation}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-lg transition-colors"
                          style={{
                            color: isGettingLocation
                              ? (personalization.button.primary || '#2563EB')
                              : (personalization.text.secondary || '#9CA3AF'),
                            backgroundColor: isGettingLocation
                              ? `${personalization.button.primary || '#2563EB'}20`
                              : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (!isGettingLocation) {
                              e.currentTarget.style.color = personalization.button.primary || '#2563EB';
                              e.currentTarget.style.backgroundColor = `${personalization.button.primary || '#2563EB'}10`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isGettingLocation) {
                              e.currentTarget.style.color = personalization.text.secondary || '#9CA3AF';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                          disabled={isGettingLocation}
                          title={t('orderForm.pickupSearch.locationHint')}
                        >
                          {isGettingLocation ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" style={{ color: personalization.icon.secondary }}>
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <p
                        className="mt-1 text-xs"
                        style={{ color: personalization.text.secondary }}
                      >
                        {t('orderForm.pickupSearch.locationHint')}
                      </p>
                    </div>

                    {(() => {
                      const isButtonDisabled = loadingPickupPoints || pickupSearchPostalCode.length < 3;
                      const buttonStyles = isButtonDisabled ? {
                        style: {
                          backgroundColor: personalization.button.disabledBackground,
                          background: '',
                          color: personalization.button.disabledText,
                        },
                        className: `w-full py-3 px-4 text-sm font-medium mb-3 transition-colors ${personalization.button.borderRadius || 'rounded-2xl'} cursor-not-allowed`,
                      } : getButtonStyles();

                      return (
                        <button
                          type="button"
                          data-theme-button="primary"
                          onClick={() => searchPickupPointsByLocation()}
                          className={`w-full py-3 px-4 text-sm font-medium mb-3 transition-colors ${buttonStyles.className}`}
                          style={buttonStyles.style}
                          disabled={isButtonDisabled}
                          ref={(el) => {
                            if (el) {
                              if (!isButtonDisabled) {
                                // Forcer la couleur du texte et du background au montage
                                const textColor = personalization.button.primaryText || '#FFFFFF';
                                el.style.setProperty('color', textColor, 'important');
                                if (personalization.button.primary) {
                                  if (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient')) {
                                    el.style.setProperty('background', personalization.button.primary, 'important');
                                    el.style.removeProperty('background-color');
                                  } else {
                                    el.style.setProperty('background-color', personalization.button.primary, 'important');
                                    el.style.removeProperty('background');
                                    el.style.removeProperty('background-image');
                                  }
                                }
                              } else {
                                const textColor = personalization.button.disabledText || '#6B7280';
                                el.style.setProperty('color', textColor, 'important');
                                el.style.setProperty('background-color', personalization.button.disabledBackground || '#D1D5DB', 'important');
                                el.style.removeProperty('background');
                                el.style.removeProperty('background-image');
                              }
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (!isButtonDisabled) {
                              e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                              if (personalization.button.primaryHover) {
                                applyBackgroundStyle(e.currentTarget, personalization.button.primaryHover);
                              }
                            } else {
                              e.currentTarget.style.setProperty('color', personalization.button.disabledText || '#9CA3AF', 'important');
                              e.currentTarget.style.setProperty('background-color', personalization.button.disabledBackground || '#D1D5DB', 'important');
                              e.currentTarget.style.removeProperty('background');
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isButtonDisabled) {
                              e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                              if (personalization.button.primary) {
                                applyBackgroundStyle(e.currentTarget, personalization.button.primary);
                              }
                            } else {
                              e.currentTarget.style.setProperty('color', personalization.button.disabledText || '#9CA3AF', 'important');
                              e.currentTarget.style.setProperty('background-color', personalization.button.disabledBackground || '#D1D5DB', 'important');
                              e.currentTarget.style.removeProperty('background');
                            }
                          }}
                        >
                          {loadingPickupPoints ? t('orderForm.pickupSearch.searching') : t('orderForm.pickupSearch.searchButton')}
                        </button>
                      );
                    })()}

                    {/* Results */}
                    {loadingPickupPoints ? (
                      <div className="flex items-center justify-center py-4">
                        <div
                          className="animate-spin rounded-full h-6 w-6 border-b-2"
                          style={{ borderColor: personalization.icon.primary }}
                        ></div>
                        <span
                          className="ml-2 text-sm"
                          style={{ color: personalization.text.secondary }}
                        >
                          {t('orderForm.pickupSearch.searchingPoints')}
                        </span>
                      </div>
                    ) : pickupPoints.length > 0 ? (
                      <div>
                        <h4
                          className="text-sm font-medium mb-3"
                          style={{ color: personalization.icon.primary }}
                        >
                          {t('orderForm.pickupSearch.selectTitle')}
                        </h4>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {pickupPoints.slice(0, 5).map((point) => (
                            <label
                              key={point.id}
                              aria-label={t('orderForm.pickupSearch.ariaSelectPickup', {
                                name: point.name,
                                address: point.address,
                                city: point.city,
                              })}
                              className="flex items-start p-3 border rounded-lg cursor-pointer transition-all"
                              style={{
                                borderColor: selectedPickupPoint?.id === point.id
                                  ? getPrimaryColor(personalization.button.primary)
                                  : (personalization.input.border || '#E5E7EB'),
                                backgroundColor: selectedPickupPoint?.id === point.id
                                  ? getColorWithOpacity(personalization.button.primary, 0.1)
                                  : 'transparent',
                              }}
                              onMouseEnter={(e) => {
                                if (selectedPickupPoint?.id !== point.id) {
                                  e.currentTarget.style.borderColor = personalization.input.border || '#D1D5DB';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedPickupPoint?.id !== point.id) {
                                  e.currentTarget.style.borderColor = personalization.input.border || '#E5E7EB';
                                }
                              }}
                            >
                              <input
                                type="radio"
                                name="pickupPoint"
                                className="mt-1 mr-3 focus:ring-2"
                                style={{
                                  color: getPrimaryColor(personalization.button.primary),
                                  '--tw-ring-color': getPrimaryColor(personalization.button.primary),
                                } as any}
                                checked={selectedPickupPoint?.id === point.id}
                                onChange={() => setSelectedPickupPoint(point)}
                              />
                              <div className="flex-1">
                                <div
                                  className="font-medium text-sm"
                                  style={{ color: personalization.text.primary }}
                                >
                                  {point.name}
                                </div>
                                <div
                                  className="text-xs"
                                  style={{ color: personalization.text.secondary }}
                                >
                                  {point.address}, {point.city}
                                </div>

                                {/* Horaires d'ouverture */}
                                {point.openingHours && (
                                  <div
                                    className="mt-2 pt-2 border-t"
                                    style={{ borderColor: personalization.card.border || '#F3F4F6' }}
                                  >
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                      <svg
                                        className="w-3.5 h-3.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        style={{ color: personalization.icon.secondary }}
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span
                                        className="text-xs font-medium uppercase tracking-wide"
                                        style={{ color: personalization.text.secondary }}
                                      >
                                        {t('orderForm.pickupSearch.openingHoursLabel')}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                                      {point.openingHours.split(' | ').map((dayHours) => {
                                        const colonIndex = dayHours.indexOf(': ');
                                        const day = colonIndex > 0 ? dayHours.substring(0, colonIndex) : dayHours;
                                        const hours = colonIndex > 0 ? dayHours.substring(colonIndex + 2) : '';
                                        const isClosed = hours === 'Fermé' || !hours;
                                        return (
                                          <div key={`${point.id}-${day}`} className="flex items-start">
                                            <span
                                              className="font-medium min-w-[2.5rem]"
                                              style={{ color: personalization.text.secondary }}
                                            >
                                              {day}:
                                            </span>
                                            <span style={{ color: isClosed ? personalization.text.secondary : personalization.text.primary }}>
                                              {hours || 'Fermé'}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                <div
                                  className="text-xs mt-1"
                                  style={{ color: personalization.text.secondary }}
                                >
                                  {point.distance ? `${point.distance.toFixed(1)} km` : ''}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : hasSearchedPickupPoints ? (
                      <p
                        className="text-sm"
                        style={{ color: personalization.text.secondary }}
                      >
                        {t('orderForm.pickupSearch.noPointsFound')}
                      </p>
                    ) : null}

                    {/* Message d'aide si point relais requis mais non sélectionné */}
                    {(() => {
                      const selectedMethod = getShippingMethodById(form.watch('deliveryMethod') || '');
                      const isHomeDelivery = selectedMethod?.isHomeDelivery === true;
                      const isPickupMethod = !isHomeDelivery && selectedMethod;
                      const needsPickupSelection = isPickupMethod && !selectedPickupPoint && !loadingPickupPoints;

                      if (needsPickupSelection && (pickupPoints.length === 0 || hasSearchedPickupPoints)) {
                        return (
                          <div
                            className="mt-3 p-3 rounded-lg border"
                            style={{
                              backgroundColor: '#FEF3C7',
                              borderColor: '#FCD34D',
                            }}
                          >
                            <p
                              className="text-sm font-medium"
                              style={{ color: '#92400E' }}
                            >
                              {t('orderForm.pickupSearch.required') || '⚠️ Veuillez sélectionner un point relais pour continuer'}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

            </div>

          )}

        {/* Submit Button Section */}
        <div
          className={`p-6 ${personalization.card.borderRadius || 'rounded-2xl'} ${personalization.card.shadow || 'shadow-sm'}`}
          style={{
            backgroundColor: personalization.card.background,
            borderColor: personalization.card.border,
            borderWidth: personalization.card.border ? '1px' : '0',
            borderStyle: personalization.card.border ? 'solid' : 'none',
          }}
        >
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            {(() => {
              // Pour les commandes fusionnées, seules les informations de base sont requises
              if (isMergedOrder) {
                const canSubmit = buttonValidation.canSubmit;

                const buttonStyle: React.CSSProperties = canSubmit ? (
                  personalization.button.primary && (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient'))
                    ? {
                      background: personalization.button.primary,
                    }
                    : {
                      backgroundColor: personalization.button.primary || '#9333EA',
                    }
                ) : {
                  backgroundColor: personalization.button.disabledBackground || '#D1D5DB',
                };
                // Forcer la couleur du texte directement dans le style
                buttonStyle.color = canSubmit
                  ? (personalization.button.primaryText || '#FFFFFF')
                  : (personalization.button.disabledText || '#6B7280');

                return (
                  <button
                    data-theme-button="primary"
                    type="submit"
                    className={`w-full py-4 px-6 font-semibold text-lg transition-all shadow-lg transform active:scale-[0.98] ${personalization.button.borderRadius || 'rounded-2xl'} ${!canSubmit ? 'cursor-not-allowed opacity-80' : 'hover:scale-[1.02]'}`}
                    style={buttonStyle}
                    disabled={isLoading}
                    ref={(el) => {
                      // Forcer la couleur du texte et du background au montage avec !important via setProperty
                      if (el) {
                        const textColor = personalization.button.primaryText || '#FFFFFF';
                        el.style.setProperty('color', textColor, 'important');
                        if (personalization.button.primary) {
                          if (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient')) {
                            el.style.setProperty('background', personalization.button.primary, 'important');
                            el.style.removeProperty('background-color');
                          } else {
                            el.style.setProperty('background-color', personalization.button.primary, 'important');
                            el.style.removeProperty('background');
                            el.style.removeProperty('background-image');
                          }
                        }
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (canSubmit && !e.currentTarget.disabled) {
                        e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                        if (personalization.button.primaryHover) {
                          applyBackgroundStyle(e.currentTarget, personalization.button.primaryHover);
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canSubmit && !e.currentTarget.disabled) {
                        e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                        if (personalization.button.primary) {
                          applyBackgroundStyle(e.currentTarget, personalization.button.primary);
                        }
                      }
                    }}
                    onMouseDown={(e) => {
                      if (canSubmit && !e.currentTarget.disabled) {
                        e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                        if (personalization.button.primaryHover) {
                          applyBackgroundStyle(e.currentTarget, personalization.button.primaryHover);
                        }
                      }
                    }}
                    onMouseUp={(e) => {
                      if (canSubmit && !e.currentTarget.disabled) {
                        e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                        if (personalization.button.primaryHover) {
                          applyBackgroundStyle(e.currentTarget, personalization.button.primaryHover);
                        }
                      }
                    }}
                    onFocus={(e) => {
                      if (canSubmit && !e.currentTarget.disabled) {
                        e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                      }
                    }}
                    onBlur={(e) => {
                      if (canSubmit && !e.currentTarget.disabled) {
                        e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                        if (personalization.button.primary) {
                          applyBackgroundStyle(e.currentTarget, personalization.button.primary);
                        }
                      }
                    }}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" style={{ color: personalization.button.primaryText || '#FFFFFF' }}>
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('orderForm.buttons.addingProduct')}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" style={{ color: personalization.button.primaryText || '#FFFFFF' }}>
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {t('orderForm.buttons.addToOrder')}
                      </div>
                    )}
                  </button>
                );
              }

              // Logique normale pour les nouvelles commandes
              // Utiliser la validation calculée avec useMemo
              const canSubmit = buttonValidation.canSubmit;
              const hasDeliveryMethod = buttonValidation.hasDeliveryMethod;
              const isPickupRequired = buttonValidation.isPickupRequired;

              const buttonStyle: React.CSSProperties = canSubmit ? (
                personalization.button.primary && (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient'))
                  ? {
                    background: personalization.button.primary,
                  }
                  : {
                    backgroundColor: personalization.button.primary || '#9333EA',
                  }
              ) : {
                backgroundColor: personalization.button.disabledBackground || '#D1D5DB',
              };
              // Forcer la couleur du texte directement dans le style
              buttonStyle.color = canSubmit
                ? (personalization.button.primaryText || '#FFFFFF')
                : (personalization.button.disabledText || '#6B7280');

              return (
                <button
                  data-theme-button="primary"
                  type="submit"
                  className={`w-full py-4 px-6 font-semibold text-lg transition-all shadow-lg transform active:scale-[0.98] ${personalization.button.borderRadius || 'rounded-2xl'} ${!canSubmit ? 'cursor-not-allowed opacity-80' : 'hover:scale-[1.02]'}`}
                  style={buttonStyle}
                  disabled={isLoading}
                  ref={(el) => {
                    // Forcer la couleur du texte et du background au montage avec !important via setProperty
                    if (el) {
                      const textColor = personalization.button.primaryText || '#FFFFFF';
                      el.style.setProperty('color', textColor, 'important');
                      if (personalization.button.primary) {
                        if (personalization.button.primary.includes('gradient') || personalization.button.primary.includes('linear-gradient')) {
                          el.style.setProperty('background', personalization.button.primary, 'important');
                          el.style.removeProperty('background-color');
                        } else {
                          el.style.setProperty('background-color', personalization.button.primary, 'important');
                          el.style.removeProperty('background');
                          el.style.removeProperty('background-image');
                        }
                      }
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (canSubmit && !e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                      if (personalization.button.primaryHover) {
                        applyBackgroundStyle(e.currentTarget, personalization.button.primaryHover);
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (canSubmit && !e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                      if (personalization.button.primary) {
                        applyBackgroundStyle(e.currentTarget, personalization.button.primary);
                      }
                    }
                  }}
                  onMouseDown={(e) => {
                    if (canSubmit && !e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                      if (personalization.button.primaryHover) {
                        applyBackgroundStyle(e.currentTarget, personalization.button.primaryHover);
                      }
                    }
                  }}
                  onMouseUp={(e) => {
                    if (canSubmit && !e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                      if (personalization.button.primaryHover) {
                        applyBackgroundStyle(e.currentTarget, personalization.button.primaryHover);
                      }
                    }
                  }}
                  onFocus={(e) => {
                    if (canSubmit && !e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                    }
                  }}
                  onBlur={(e) => {
                    if (canSubmit && !e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
                      if (personalization.button.primary) {
                        applyBackgroundStyle(e.currentTarget, personalization.button.primary);
                      }
                    }
                  }}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" style={{ color: personalization.button.primaryText || '#FFFFFF' }}>
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('orderForm.buttons.creatingOrder')}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" style={{ color: personalization.button.primaryText || '#FFFFFF' }}>
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('orderForm.buttons.confirmAndPay')}
                    </div>
                  )}
                </button>
              );
            })()}

          </form>
        </div>

        {/* Footer with contact information */}
        {configuration && (
          configuration.website ||
          savContacts.length > 0 ||
          configuration.instagramPage ||
          configuration.facebookPage ||
          configuration.tiktokPage ||
          (configuration.socials && Object.keys(configuration.socials).length > 0)
        ) && (
            <div
              className={`p-6 mt-6 ${getCardStyles().className}`}
              style={{
                backgroundColor: personalization.card.background,
                borderColor: personalization.card.border,
                ...getCardStyles().style
              }}
            >
              <div className="text-center">
                <h4
                  className="text-sm font-medium mb-4"
                  style={{ color: personalization.text.heading }}
                >
                  {t('orderForm.footer.contactUs')}
                </h4>
                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {savContacts.map((contact, index) => {
                    if (!contact.value) return null;
                    const Icon = getContactIcon(contact.type);
                    const link = getContactLink(contact.type, contact.value);
                    const label = contact.label || (contact.type === 'email' ? 'Email' : contact.type === 'phone' ? 'Téléphone' : 'WhatsApp');
                    // Utiliser les couleurs par défaut pour les contacts (vert pour WhatsApp, bleu pour téléphone, gris pour email)
                    const bgColor = contact.type === 'whatsapp'
                      ? '#D1FAE5'
                      : contact.type === 'phone'
                        ? '#DBEAFE'
                        : '#F3F4F6';
                    const hoverBgColor = contact.type === 'whatsapp'
                      ? '#A7F3D0'
                      : contact.type === 'phone'
                        ? '#BFDBFE'
                        : '#E5E7EB';
                    const textColor = contact.type === 'whatsapp'
                      ? '#047857'
                      : contact.type === 'phone'
                        ? '#1E40AF'
                        : '#374151';
                    return (
                      <a
                        key={`sav-${index}`}
                        href={link}
                        target={contact.type === 'whatsapp' ? "_blank" : undefined}
                        rel={contact.type === 'whatsapp' ? "noopener noreferrer" : undefined}
                        className="inline-flex items-center justify-center w-12 h-12 rounded-full transition-colors"
                        style={{
                          backgroundColor: bgColor,
                          color: textColor,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = hoverBgColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = bgColor;
                        }}
                        title={label}
                      >
                        <Icon className="w-5 h-5" />
                      </a>
                    );
                  })}
                </div>
                {(configuration.website ||
                  configuration.instagramPage ||
                  configuration.facebookPage ||
                  configuration.tiktokPage ||
                  (configuration.socials && Object.keys(configuration.socials).length > 0)) && (
                    <>
                      <h4
                        className="text-sm font-medium mb-3"
                        style={{ color: personalization.text.heading }}
                      >
                        {t('admin.configuration.contact.socials.title') || 'Réseaux sociaux'}
                      </h4>
                      <div className="flex flex-wrap justify-center gap-3">
                        {configuration.website && (
                          <a
                            href={configuration.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                            title={t('orderForm.footer.website')}
                          >
                            <Globe className="w-5 h-5" />
                          </a>
                        )}
                        {/* Instagram (Legacy) */}
                        {configuration.instagramPage && (
                          <a
                            href={configuration.instagramPage.startsWith('http') ? configuration.instagramPage : `https://instagram.com/${configuration.instagramPage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-700 transition-colors"
                            title={t('orderForm.footer.instagram')}
                          >
                            <Instagram className="w-5 h-5" />
                          </a>
                        )}

                        {/* Facebook (Legacy) */}
                        {configuration.facebookPage && (
                          <a
                            href={configuration.facebookPage.startsWith('http') ? configuration.facebookPage : `https://facebook.com/${configuration.facebookPage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                            title={t('orderForm.footer.facebook')}
                          >
                            <Facebook className="w-5 h-5" />
                          </a>
                        )}

                        {/* TikTok (Legacy) */}
                        {configuration.tiktokPage && (
                          <a
                            href={configuration.tiktokPage.startsWith('http') ? configuration.tiktokPage : `https://tiktok.com/@${configuration.tiktokPage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                            title={t('orderForm.footer.tiktok')}
                          >
                            <TikTokIcon className="w-5 h-5" />
                          </a>
                        )}

                        {/* New Dynamic Socials */}
                        {configuration.socials && Object.entries(configuration.socials).map(([key, value]) => {
                          const Icon = getSocialIcon(key);
                          return (
                            <a
                              key={key}
                              href={value.startsWith('http') ? value : `https://${value}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                              title={key === 'website' ? 'Site Web' : key}
                            >
                              <Icon className="w-5 h-5" />
                            </a>
                          );
                        })}
                      </div>
                    </>
                  )}

                {/* Legal Links */}
                {configuration && (
                  <div className="mt-6 pt-6 border-t" style={{ borderColor: personalization.card.border }}>
                    <h4
                      className="text-sm font-medium mb-3"
                      style={{ color: personalization.text.heading }}
                    >
                      📋 {t('orderForm.legalLinks.title')}
                    </h4>
                    <div className="flex flex-wrap justify-center gap-3 text-sm">
                      {/* CGV */}
                      <a
                        href={`/orga/${organizationId}/legal?section=cgv`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 hover:underline transition-colors"
                        style={{ color: personalization.button.primary || '#9333EA' }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('orderForm.legalLinks.termsAndConditions')}
                      </a>

                      {/* Mentions légales */}
                      <a
                        href={`/orga/${organizationId}/legal?section=mentions`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 hover:underline transition-colors"
                        style={{ color: personalization.button.primary || '#9333EA' }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                        </svg>
                        {t('orderForm.legalLinks.legalNotice')}
                      </a>

                      {/* Politique de confidentialité */}
                      <a
                        href={`/orga/${organizationId}/legal?section=privacy`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 hover:underline transition-colors"
                        style={{ color: personalization.button.primary || '#9333EA' }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        {t('orderForm.legalLinks.privacyPolicy')}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
      <style>{`
        .wysiwyg-rendered-content ul { list-style-type: disc !important; padding-left: 1.5rem !important; margin-bottom: 0.5rem !important; display: block !important; }
        .wysiwyg-rendered-content ol { list-style-type: decimal !important; padding-left: 1.5rem !important; margin-bottom: 0.5rem !important; display: block !important; }
        .wysiwyg-rendered-content li { display: list-item !important; margin-bottom: 0.25rem !important; }
        .wysiwyg-rendered-content p { margin-bottom: 0.5rem !important; }
        .wysiwyg-rendered-content strong { font-weight: bold !important; }
        .wysiwyg-rendered-content em { font-style: italic !important; }
      `}</style>
    </>
  );
};

export default OrderForm;
